import { Room } from '../types';
import { ExportData } from '../utils/exportUtils';
import { Capacitor } from '@capacitor/core';
import { Filesystem, Directory, Encoding } from '@capacitor/filesystem';
import { Share } from '@capacitor/share';

export interface PuppeteerPDFOptions {
  format?: 'A4' | 'Letter';
  orientation?: 'portrait' | 'landscape';
  margin?: {
    top?: string;
    right?: string;
    bottom?: string;
    left?: string;
  };
  logoDataUrl?: string;
  headerColor?: string;
  displayHeaderFooter?: boolean;
  printBackground?: boolean;
}

/**
 * Generates PDF from HTML template using Puppeteer-style rendering
 * This mimics Puppeteer's page.pdf() functionality in the browser
 */
// Helper function to fetch SVG and convert to PNG data URL (jsPDF doesn't support SVG)
async function fetchSVGAsPNG(svgPath: string, width: number = 600, height: number = 600): Promise<string> {
  try {
    const response = await fetch(svgPath);
    const svgText = await response.text();
    
    // Create an image element with the SVG
    const img = new Image();
    const svgBlob = new Blob([svgText], { type: 'image/svg+xml;charset=utf-8' });
    const url = URL.createObjectURL(svgBlob);
    
    // Wait for image to load
    await new Promise<void>((resolve, reject) => {
      img.onload = () => resolve();
      img.onerror = reject;
      img.src = url;
    });
    
    // Draw SVG to canvas
    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Failed to get canvas context');
    
    ctx.fillStyle = 'white';
    ctx.fillRect(0, 0, width, height);
    ctx.drawImage(img, 0, 0, width, height);
    
    // Clean up
    URL.revokeObjectURL(url);
    
    // Convert to PNG data URL
    return canvas.toDataURL('image/png', 0.95);
  } catch (error) {
    console.error('Failed to convert SVG to PNG:', svgPath, error);
    return '';
  }
}

export class PuppeteerPDFService {
  private static defaultOptions: PuppeteerPDFOptions = {
    format: 'A4',
    orientation: 'portrait',
    margin: {
      top: '5mm',
      right: '5mm', 
      bottom: '5mm',
      left: '5mm'
    },
    displayHeaderFooter: false,
    printBackground: true
  };

  /**
   * Generates PDF using jsPDF (same as working puppeteer export)
   * This uses the proven approach from puppeteerExportUtils
   */
  static async generatePDFFromHTML(
    htmlContent: string,
    filename: string = 'report.pdf',
    options: PuppeteerPDFOptions = {},
    data?: ExportData
  ): Promise<void> {
    const jsPDF = (await import('jspdf')).default;
    
    try {
      // DEBUG: Log entry point
      console.log('🚀 PDF GENERATION ENTRY POINT:', {
        hasData: !!data,
        dataKeys: data ? Object.keys(data) : 'DATA IS UNDEFINED',
        filename,
        htmlContentLength: htmlContent?.length || 0
      });

      // Create PDF using jsPDF (same as working puppeteer export)
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
        compress: true
      });
      
      // Generate pages for all floors with their floor plans and element keys
      if (data) {
        console.log('✅ Data exists, calling generateAllFloorPages...');
        await this.generateAllFloorPages(pdf, data, options.logoDataUrl);
      } else {
        console.log('❌ No data provided, using fallback...');
        // Fallback for missing data
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(10);
        pdf.text('No export data available', 20, 20);
      }
      
      // Use the same download logic as working puppeteer export
      if (Capacitor.isNativePlatform()) {
        // Mobile: Use Capacitor filesystem + share
        await this.savePDFMobile(pdf, filename);
      } else {
        // Desktop: Direct download
        await this.savePDFDesktop(pdf, filename);
      }
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      throw error;
    }
  }

  /**
   * Generate pages for all floors with their floor plans and element keys
   */
  private static async generateAllFloorPages(pdf: any, data: ExportData, logoDataUrl?: string): Promise<void> {
    const { siteNotesData, propertyAddress, currentWalls, elements, rooms, textBoxes } = data;
    
    if (!siteNotesData?.floors || siteNotesData.floors.length === 0) {
      // Fallback to single page if no floor data
      await this.generateDetailedFloorPlan(pdf, data);
      return;
    }
    
    // 🔧 EXTENSION FIX: Use same logic as jsPDF exportComprehensivePNG
    // Only generate floor plan pages for floors with actual floor plan data
    // Highlighter extensions are included in calculations but not as separate pages
    const allFloorPlans = data.allFloorPlans || { [data.currentFloor]: currentWalls };
    const availableFloors = Object.keys(allFloorPlans).map(Number).sort((a, b) => a - b);
    
    console.log(`📄 CLIENT PDF: Found ${siteNotesData.floors.length} total site notes floors, generating ${availableFloors.length} floor plan pages`);
    console.log(`📄 CLIENT PDF: availableFloors:`, availableFloors);
    
    // Generate a page for each floor with actual floor plan data (matching jsPDF logic)
    for (let i = 0; i < availableFloors.length; i++) {
      const floorNumber = availableFloors[i];
      // Find corresponding site notes floor data
      const floor = siteNotesData.floors.find(f => f.id === floorNumber) || {
        id: floorNumber,
        floorLevel: floorNumber === 1 ? 'ground floor' : `floor ${floorNumber}`,
        buildingType: 'main building',
        height: '',
        floorArea: '',
        hlp: '',
        pw: '',
        rooms: []
      };
      
      // Add new page for each floor except the first one
      if (i > 0) {
        pdf.addPage();
      }
      
      // Extract floor-specific data from allFloorData
      const floorData = data.allFloorData?.[floorNumber];
      const floorWalls = floorData?.walls || (floorNumber === data.currentFloor ? currentWalls : []);
      const floorElements = floorData?.elements || elements.filter((el: any) => el.floor === floorNumber);
      const floorRooms = floorData?.rooms || (floorNumber === data.currentFloor ? rooms : []);
      const floorTextBoxes = floorData?.textBoxes || (floorNumber === data.currentFloor ? textBoxes : []);
      
      console.log(`📄 PDF Floor ${floorNumber} data:`, {
        walls: floorWalls?.length || 0,
        elements: floorElements?.length || 0,
        rooms: floorRooms?.length || 0,
        textBoxes: floorTextBoxes?.length || 0,
        hasFloorData: !!floorData,
        floorWallsUndefined: floorWalls === undefined,
        floorElementsUndefined: floorElements === undefined,
        floorRoomsUndefined: floorRooms === undefined,
        originalDataStructure: {
          hasCurrentWalls: !!currentWalls,
          currentWallsLength: currentWalls?.length || 0,
          hasElements: !!elements,
          elementsLength: elements?.length || 0,
          hasAllFloorData: !!data.allFloorData,
          allFloorDataKeys: data.allFloorData ? Object.keys(data.allFloorData) : []
        }
      });
      
      // Create floor-specific data for this page
      const floorSpecificData = {
        ...data,
        currentFloor: floorNumber,
        currentWalls: floorWalls,
        elements: floorElements,
        rooms: floorRooms,
        textBoxes: floorTextBoxes
      };
      
      const floorName = `${floor.floorLevel === 'ground' ? 'Ground Floor' : 
                         floor.floorLevel === 'first' ? '1st Floor' :
                         floor.floorLevel === 'second' ? '2nd Floor' :
                         floor.floorLevel === 'third' ? '3rd Floor' :
                         floor.floorLevel || 'Floor'} - ${floor.buildingType === 'main building' ? 'Main Building' : 
                         floor.buildingType.charAt(0).toUpperCase() + floor.buildingType.slice(1)}`;
      
      await this.generateDetailedFloorPlan(pdf, floorSpecificData, floorName, i + 1);
    }
    
    // Add Room in Roof page if data exists AND toggle is set to 'yes'
    const rirData = siteNotesData?.formData?.roomInRoofData;
    const hasRoomInRoof = siteNotesData?.formData?.hasRoomInRoof === 'yes';
    if (hasRoomInRoof && rirData && rirData.measurements && rirData.measurements.length > 0) {
      console.log('📐 Adding Room in Roof page to PDF...');
      await this.addRoomInRoofPage(pdf, siteNotesData, data.propertyAddress || 'Property', availableFloors.length + 1, logoDataUrl);
    } else if (rirData && rirData.measurements && rirData.measurements.length > 0 && !hasRoomInRoof) {
      console.log('📐 Skipping Room in Roof page - toggle is not enabled');
    }
  }

  /**
   * Add Room in Roof page to PDF using jsPDF
   */
  private static async addRoomInRoofPage(pdf: any, siteNotesData: any, propertyAddress: string, pageNumber: number, logoDataUrl?: string): Promise<void> {
    const rirData = siteNotesData?.formData?.roomInRoofData;
    
    console.log('🎨 ADDING RIR PAGE:', {
      hasRirData: !!rirData,
      hasDiagramImage: !!rirData?.diagramImage,
      imageLength: rirData?.diagramImage?.length,
      measurementsCount: rirData?.measurements?.length
    });
    
    if (!rirData || !rirData.diagramImage) {
      console.warn('⚠️ RIR data or diagram image missing, skipping page');
      return;
    }
    
    // Add new page
    pdf.addPage();
    
    const pageWidth = 210; // A4 width in mm
    const pageHeight = 297; // A4 height in mm
    const margin = 15;
    
    // Add header
    pdf.setFontSize(16);
    pdf.setFont('helvetica', 'bold');
    pdf.text(propertyAddress, margin, margin + 5);
    
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'normal');
    pdf.text('Room in Roof Assessment', margin, margin + 12);
    
    pdf.setFontSize(10);
    pdf.text(`Page ${pageNumber}`, pageWidth - margin - 15, margin + 5);
    
    // Add logo if available
    if (logoDataUrl) {
      try {
        pdf.addImage(logoDataUrl, 'PNG', pageWidth - margin - 30, margin, 25, 10);
      } catch (error) {
        console.warn('Failed to add logo:', error);
      }
    }
    
    // Add separator line
    pdf.setDrawColor(234, 179, 8); // Yellow
    pdf.setLineWidth(0.5);
    pdf.line(margin, margin + 15, pageWidth - margin, margin + 15);
    
    // Add diagram title
    const diagramType = rirData.diagramType || 'standard';
    const diagramTitle = diagramType === 'with-dormer' ? 'ROOM IN ROOF WITH DORMER' : 'STANDARD ROOM IN ROOF';
    
    pdf.setFontSize(14);
    pdf.setFont('helvetica', 'bold');
    pdf.setTextColor(41, 98, 255); // Blue
    pdf.text(diagramTitle, margin, margin + 25);
    pdf.setTextColor(0, 0, 0); // Reset to black
    
    // Add diagram image
    try {
      const imgWidth = pageWidth - (2 * margin);
      const imgHeight = 100; // Fixed height, will auto-scale
      
      console.log('📷 Adding diagram image to PDF:', {
        x: margin,
        y: margin + 30,
        width: imgWidth,
        height: imgHeight,
        imageDataLength: rirData.diagramImage.length
      });
      
      pdf.addImage(
        rirData.diagramImage,
        'PNG',
        margin,
        margin + 30,
        imgWidth,
        imgHeight,
        undefined,
        'FAST'
      );
    } catch (error) {
      console.error('❌ Failed to add diagram image:', error);
      pdf.setFontSize(10);
      pdf.setTextColor(150, 150, 150);
      pdf.text('Diagram could not be loaded', pageWidth / 2, margin + 80, { align: 'center' });
      pdf.setTextColor(0, 0, 0);
    }
    
    // Add measurements table
    const tableStartY = margin + 140;
    pdf.setFontSize(12);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Area Details', margin, tableStartY);
    
    // Table headers
    const tableY = tableStartY + 8;
    const colWidths = [50, 30, 30, 30, 35];
    const colX = [margin, margin + colWidths[0], margin + colWidths[0] + colWidths[1], margin + colWidths[0] + colWidths[1] + colWidths[2], margin + colWidths[0] + colWidths[1] + colWidths[2] + colWidths[3]];
    
    pdf.setFillColor(240, 240, 240);
    pdf.rect(margin, tableY, pageWidth - (2 * margin), 8, 'F');
    
    pdf.setFontSize(9);
    pdf.setFont('helvetica', 'bold');
    pdf.text('Area', colX[0] + 2, tableY + 5);
    pdf.text('Length (m)', colX[1] + 2, tableY + 5);
    pdf.text('Height (m)', colX[2] + 2, tableY + 5);
    pdf.text('Insulation', colX[3] + 2, tableY + 5);
    pdf.text('Insulation Type', colX[4] + 2, tableY + 5);
    
    // Table rows
    pdf.setFont('helvetica', 'normal');
    let rowY = tableY + 8;
    
    rirData.measurements
      .filter((m: any) => m.visible !== false)
      .forEach((m: any, index: number) => {
        if (rowY > pageHeight - margin - 10) {
          // Start new page if needed
          pdf.addPage();
          rowY = margin + 10;
        }
        
        // Alternating row colors
        if (index % 2 === 0) {
          pdf.setFillColor(250, 250, 250);
          pdf.rect(margin, rowY, pageWidth - (2 * margin), 7, 'F');
        }
        
        pdf.text(m.label || '-', colX[0] + 2, rowY + 5);
        pdf.text(m.length || '-', colX[1] + 2, rowY + 5);
        pdf.text(m.height || '-', colX[2] + 2, rowY + 5);
        pdf.text(m.insulation || '-', colX[3] + 2, rowY + 5);
        pdf.text(m.insulationType || '-', colX[4] + 2, rowY + 5);
        
        rowY += 7;
      });
    
    console.log('✅ RIR page added successfully');
  }

  /**
   * Generate detailed floor plan with element key (matching jsPDF export)
   */
  private static async generateDetailedFloorPlan(pdf: any, data: ExportData, floorTitle?: string, pageNumber?: number): Promise<void> {
    const {
      currentWalls,
      elements,
      rooms,
      roomCustomLabels,
      propertyAddress,
      currentFloor,
      siteNotesData
    } = data;

    // PDF constants
    const margin = 5;
    const pageWidth = 210;
    const pageHeight = 297;
    const contentWidth = pageWidth - margin * 2;
    let yPos = margin;

    // Helper function to get best address
    const getBestAddress = (propAddress: string, siteAddress?: any) => {
      if (siteAddress) {
        if (typeof siteAddress === 'object' && siteAddress.houseNumber) {
          return `${siteAddress.houseNumber} ${siteAddress.street}, ${siteAddress.town} ${siteAddress.postcode}`;
        }
        if (typeof siteAddress === 'string' && siteAddress.trim() !== '') {
          return siteAddress;
        }
      }
      return propAddress || 'Property Assessment Report';
    };

    const bestAddress = getBestAddress(propertyAddress, siteNotesData?.formData?.address);

    // Add header with floor-specific title
    const headerTitle = floorTitle || 'Detailed Floor Plan';
    yPos = this.addPDFHeader(pdf, bestAddress, headerTitle, yPos, margin, pageWidth, pageNumber);

    // Generate actual floor plan canvas (matching jsPDF export)
    try {
      // Import the canvas generation function
      const { generateEnhancedFloorPlanCanvas } = await import('../utils/exportUtils');
      
      // DEBUG: Log data being passed to canvas generator
      console.log(`🎨 CANVAS GENERATOR INPUT DATA FOR FLOOR ${currentFloor}:`, {
        hasData: !!data,
        hasCurrentWalls: !!data?.currentWalls,
        currentWallsLength: data?.currentWalls?.length || 0,
        hasElements: !!data?.elements,
        elementsLength: data?.elements?.length || 0,
        hasRooms: !!data?.rooms,
        roomsLength: data?.rooms?.length || 0,
        currentFloor: data?.currentFloor,
        dataKeys: data ? Object.keys(data) : 'DATA IS UNDEFINED',
        wallsSample: data?.currentWalls?.slice(0, 2)?.map(w => ({ start: w.start, end: w.end }))
      });
      
      // Additional check for completely undefined data
      if (!data) {
        console.error('🚨 CRITICAL: Data object is completely undefined!');
        throw new Error('Data object is undefined');
      }
      
      // Generate the floor plan canvas
      const canvas = await generateEnhancedFloorPlanCanvas(data);
      
      if (canvas) {
        // Convert canvas to data URL
        const imgData = canvas.toDataURL('image/png', 1.0);
        
        // Calculate optimal dimensions for the floor plan
        const maxFloorPlanWidth = contentWidth;
        const maxFloorPlanHeight = 120; // Maximum height constraint
        
        const canvasAspectRatio = canvas.width / canvas.height;
        let imgWidth = maxFloorPlanWidth;
        let imgHeight = imgWidth / canvasAspectRatio;
        
        // If height exceeds maximum, constrain by height
        if (imgHeight > maxFloorPlanHeight) {
          imgHeight = maxFloorPlanHeight;
          imgWidth = imgHeight * canvasAspectRatio;
        }
        
        // Center the image horizontally
        const imgX = margin + (contentWidth - imgWidth) / 2;
        
        // Add the floor plan image to PDF
        pdf.addImage(imgData, 'PNG', imgX, yPos, imgWidth, imgHeight);
        
        yPos += imgHeight + 10;
      } else {
        // Fallback if canvas generation fails
        const floorPlanHeight = 60;
        pdf.setFillColor(248, 250, 252);
        pdf.rect(margin, yPos, contentWidth, floorPlanHeight, 'F');
        pdf.setFont('helvetica', 'normal');
        pdf.setFontSize(10);
        pdf.setTextColor(100, 116, 139);
        pdf.text('Floor plan could not be generated', margin + contentWidth/2, yPos + floorPlanHeight/2, { align: 'center' });
        yPos += floorPlanHeight + 10;
      }
    } catch (error) {
      console.error('Error generating floor plan canvas:', error);
      // Fallback if there's an error
      const floorPlanHeight = 60;
      pdf.setFillColor(248, 250, 252);
      pdf.rect(margin, yPos, contentWidth, floorPlanHeight, 'F');
      pdf.setFont('helvetica', 'normal');
      pdf.setFontSize(10);
      pdf.setTextColor(100, 116, 139);
      pdf.text('Floor plan generation error', margin + contentWidth/2, yPos + floorPlanHeight/2, { align: 'center' });
      yPos += floorPlanHeight + 10;
    }

    // Add comprehensive element key
    this.addElementKey(pdf, yPos, margin, contentWidth);
  }

  /**
   * Add PDF header (matching jsPDF export style)
   */
  private static addPDFHeader(pdf: any, address: string, sectionTitle: string, yPos: number, margin: number, pageWidth: number, pageNumber?: number): number {
    const headerHeight = 24;
    const cornerRadius = 8;
    const headerMargin = 4;
    
    // Clean white background with rounded corners
    pdf.setFillColor(255, 255, 255);
    pdf.roundedRect(headerMargin, headerMargin, pageWidth - (headerMargin * 2), headerHeight - headerMargin, cornerRadius, cornerRadius, 'F');
    
    // Subtle shadow
    pdf.setDrawColor(229, 231, 235);
    pdf.setLineWidth(0.2);
    pdf.roundedRect(headerMargin + 0.3, headerMargin + 0.3, pageWidth - (headerMargin * 2), headerHeight - headerMargin, cornerRadius, cornerRadius, 'D');
    
    // Clean border
    pdf.setDrawColor(229, 231, 235);
    pdf.setLineWidth(0.5);
    pdf.roundedRect(headerMargin, headerMargin, pageWidth - (headerMargin * 2), headerHeight - headerMargin, cornerRadius, cornerRadius, 'D');
    
    // Typography
    const addressY = headerHeight * 0.4;
    const sectionY = headerHeight * 0.75;
    const pageNumberY = headerHeight * 0.6;
    
    // Property address
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(11);
    pdf.setTextColor(17, 24, 39);
    pdf.text(address, margin + 4, addressY);
    
    // Section title
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(9);
    pdf.setTextColor(55, 65, 81);
    pdf.text(sectionTitle, margin + 4, sectionY);
    
    // Page number
    pdf.setFont('helvetica', 'normal');
    pdf.setFontSize(8);
    pdf.setTextColor(107, 114, 128);
    pdf.text(`Page ${pageNumber || 1}`, pageWidth - margin - 4, pageNumberY, { align: 'right' });
    
    // Separator line
    pdf.setDrawColor(229, 231, 235);
    pdf.setLineWidth(0.3);
    pdf.line(margin, headerHeight + 2, pageWidth - margin, headerHeight + 2);
    
    return headerHeight + 6;
  }

  /**
   * Add comprehensive element key (exact copy from jsPDF export)
   */
  private static addElementKey(pdf: any, startY: number, margin: number, contentWidth: number): void {
    // Element key data (exact copy from jsPDF export)
    const elementRows = [
      // Header Row
      [{ type: 'header', text: 'ELEMENT KEY', description: '' }],
      // Wall types and heating
      [
        { type: 'line', color: '#ff0000', text: 'HLP', description: '- Heat Loss Perimeter' },
        { type: 'line', color: '#0000ff', text: 'Party Wall', description: '- Party Wall' },
        { type: 'line', color: '#f97316', text: 'Sheltered', description: '- Sheltered Wall' },
        { type: 'element', text: 'RADT', bgColor: '#fde047', description: '- Radiator with TRVs' }
      ],
      // Heating and utilities
      [
        { type: 'element', text: 'RAD', bgColor: '#fde047', description: '- Radiator' },
        { type: 'element', text: 'H/S', bgColor: '#fde047', description: '- Heat Source' },
        { type: 'element', text: 'GM', bgColor: '#bbf7d0', description: '- Gas Meter' },
        { type: 'element', text: 'EM', bgColor: '#bbf7d0', description: '- Electric Meter' }
      ],
      // More utilities
      [
        { type: 'element', text: 'CU', bgColor: '#bbf7d0', description: '- Consumer Unit' },
        { type: 'element', text: 'TNK', bgColor: '#bbf7d0', description: '- Tank' },
        { type: 'element', text: 'FAN', bgColor: '#bbf7d0', description: '- Fan' },
        { type: 'element', text: 'V', bgColor: '#bbf7d0', description: '- Vent' }
      ],
      // Lighting and safety
      [
        { type: 'light', lightType: 'led', description: '- LED Light' },
        { type: 'light', lightType: 'standard', description: '- Standard Light' },
        { type: 'text', text: 'SA', textColor: '#ef4444', description: '- Smoke Alarm' },
        { type: 'element', text: 'LH', bgColor: '#e6f3ff', description: '- Loft Hatch' }
      ],
      // Room labels
      [
        { type: 'text', text: 'BR', description: '- Bedroom' },
        { type: 'text', text: 'BTH', description: '- Bathroom' },
        { type: 'text', text: 'LR', description: '- Living Room' },
        { type: 'text', text: 'K', description: '- Kitchen' }
      ]
    ];

    // Render element key table
    const cellWidth = contentWidth / 4;
    const headerHeight = 14;
    const cellHeight = 11;
    let yPos = startY;
    
    elementRows.forEach((row, rowIndex) => {
      const isHeader = rowIndex === 0;
      const currentCellHeight = isHeader ? headerHeight : cellHeight;
      const rowY = yPos + (rowIndex === 0 ? 0 : headerHeight + (rowIndex - 1) * cellHeight);
      
      if (isHeader) {
        // Draw header
        pdf.setFillColor(241, 245, 249); // Light gray
        pdf.rect(margin, rowY, contentWidth, currentCellHeight, 'F');
        pdf.setDrawColor(203, 213, 225);
        pdf.setLineWidth(0.3);
        pdf.rect(margin, rowY, contentWidth, currentCellHeight);
        
        // Header text
        pdf.setTextColor(15, 23, 42);
        pdf.setFont('helvetica', 'bold');
        pdf.setFontSize(9);
        pdf.text('ELEMENT KEY', margin + (contentWidth / 2), rowY + (currentCellHeight / 2), { align: 'center' });
      } else {
        // Draw data cells
        row.forEach((item: any, colIndex) => {
          const cellX = margin + (colIndex * cellWidth);
          const cellCenterY = rowY + currentCellHeight / 2;
          
          // Cell background
          pdf.setFillColor(255, 255, 255);
          pdf.rect(cellX, rowY, cellWidth, currentCellHeight, 'F');
          
          // Cell border
          pdf.setDrawColor(203, 213, 225);
          pdf.setLineWidth(0.1);
          pdf.rect(cellX, rowY, cellWidth, currentCellHeight);
          
          let elementWidth = 0;
          
          if (item.type === 'line' && item.color) {
            // Draw wall type lines
            pdf.setDrawColor(item.color);
            pdf.setLineWidth(1.2);
            pdf.line(cellX + 4, cellCenterY, cellX + 14, cellCenterY);
            elementWidth = 18;
          } else if (item.type === 'element' && item.bgColor) {
            // Draw element boxes
            this.drawElementBox(pdf, cellX + 4, cellCenterY, item.text, item.bgColor);
            elementWidth = pdf.getTextWidth(item.text) + 8;
          } else if (item.type === 'light') {
            // Draw light symbols
            if (item.lightType === 'led') {
              // Green X for LED
              pdf.setDrawColor('#22c55e');
              pdf.setLineWidth(1.2);
              pdf.line(cellX + 4, cellCenterY - 2, cellX + 8, cellCenterY + 2);
              pdf.line(cellX + 8, cellCenterY - 2, cellX + 4, cellCenterY + 2);
            } else {
              // Orange circle for Standard
              pdf.setDrawColor('#f97316');
              pdf.setLineWidth(1.2);
              pdf.circle(cellX + 6, cellCenterY, 2);
            }
            elementWidth = 12;
          } else if (item.type === 'text' && item.text) {
            // Draw text labels
            const isSmoke = item.text === 'SA';
            pdf.setTextColor(isSmoke ? '#ef4444' : '#1E293B');
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(7);
            pdf.text(item.text, cellX + 4, cellCenterY + 1);
            elementWidth = pdf.getTextWidth(item.text) + 8;
          }
          
          // Draw description
          if (item.description) {
            pdf.setTextColor(30, 41, 59);
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(6);
            pdf.text(item.description, cellX + elementWidth, cellCenterY + 1);
          }
        });
      }
    });
  }

  /**
   * Draw element box with background color
   */
  private static drawElementBox(pdf: any, x: number, y: number, text: string, bgColor: string): void {
    const padding = 0.5;
    const fontSize = 6;
    
    pdf.setFont('helvetica', 'bold');
    pdf.setFontSize(fontSize);
    const textWidth = pdf.getTextWidth(text);
    const boxWidth = textWidth + padding * 2;
    const boxHeight = fontSize + padding * 1.5;
    
    // Background
    pdf.setFillColor(bgColor);
    pdf.rect(x, y - boxHeight/2, boxWidth, boxHeight, 'F');
    
    // Border
    pdf.setDrawColor('#000000');
    pdf.setLineWidth(0.1);
    pdf.rect(x, y - boxHeight/2, boxWidth, boxHeight);
    
    // Text
    pdf.setTextColor('#000000');
    pdf.text(text, x + padding, y + fontSize/4);
  }

  /**
   * Save PDF for mobile platforms using Capacitor (copied from working puppeteer export)
   */
  private static async savePDFMobile(pdf: any, filename: string): Promise<void> {
    try {
      // Get PDF as base64 for mobile sharing (same as working puppeteer export)
      const base64Data = pdf.output('datauristring').split(',')[1];
      
      // Save to device using Capacitor
      const result = await Filesystem.writeFile({
        path: filename,
        data: base64Data,
        directory: Directory.Cache,
        recursive: true
      });
      
      // Share using native share sheet
      await Share.share({
        title: 'Property Assessment Report (HTML)',
        text: 'Property assessment report generated with HTML templates',
        url: result.uri,
        dialogTitle: 'Share Property Assessment Report'
      });
      
    } catch (error) {
      console.error('❌ Mobile PDF save failed:', error);
      throw error;
    }
  }

  /**
   * Save PDF for desktop using direct download (copied from working puppeteer export)
   */
  private static async savePDFDesktop(pdf: any, filename: string): Promise<void> {
    try {
      // Direct download for desktop (same as working puppeteer export)
      pdf.save(filename);
    } catch (error) {
      console.error(' Desktop PDF save failed:', error);
      throw error;
    }
  }

  /**
   * Generate PDF for mobile platforms using Capacitor (legacy - keeping for compatibility)
   */
  private static async generateMobilePDF(iframe: HTMLIFrameElement, filename: string): Promise<void> {
    try {
      // Convert iframe content to canvas, then to PDF blob
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      if (!ctx || !iframe.contentWindow) {
        throw new Error('Unable to create canvas context or access iframe');
      }
      
      // Set canvas size to A4 dimensions (at 96 DPI)
      const a4Width = 794;  // A4 width in pixels at 96 DPI
      const a4Height = 1123; // A4 height in pixels at 96 DPI
      canvas.width = a4Width;
      canvas.height = a4Height;
      
      // Draw white background
      ctx.fillStyle = 'white';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      // Convert canvas to blob
      const blob = await new Promise<Blob>((resolve) => {
        canvas.toBlob((blob) => {
          if (blob) resolve(blob);
        }, 'image/png');
      });
      
      if (!blob) {
        throw new Error('Failed to create image blob');
      }
      
      // Convert blob to base64 for Capacitor
      const reader = new FileReader();
      const base64Data = await new Promise<string>((resolve, reject) => {
        reader.onload = () => {
          const result = reader.result as string;
          // Remove data:image/png;base64, prefix
          resolve(result.split(',')[1]);
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      
      // Save file using Capacitor Filesystem
      const result = await Filesystem.writeFile({
        path: filename,
        data: base64Data,
        directory: Directory.Documents
      });
      
      console.log('PDF saved to:', result.uri);
      
      // Share the PDF
      await Share.share({
        title: 'Property Assessment Report',
        url: result.uri,
        dialogTitle: 'Share Property Report'
      });
      
    } catch (error) {
      console.error('Error generating mobile PDF:', error);
      // Fallback to web download
      await this.generateDesktopPDF(iframe, filename);
    }
  }

  /**
   * Generate PDF for desktop using browser print API
   */
  private static async generateDesktopPDF(iframe: HTMLIFrameElement, filename: string): Promise<void> {
    try {
      if (!iframe.contentWindow) {
        throw new Error('Unable to access iframe content window');
      }
      
      // Focus the iframe and trigger print
      iframe.contentWindow.focus();
      iframe.contentWindow.print();
      
    } catch (error) {
      console.error('Error generating desktop PDF:', error);
      throw error;
    }
  }

  /**
   * Render React component to HTML string
   * This is a simple implementation - in production you might use ReactDOMServer
   */
  static renderComponentToHTML(component: React.ReactElement): string {
    // For proof of concept, we'll create the HTML directly
    // In production, you'd use ReactDOMServer.renderToString()
    return `
      <div id="pdf-root">
        <!-- Component would be rendered here -->
        <div class="pdf-page">
          <div class="header">
            <div class="header-content">
              <div class="header-left">
                <h1 class="property-address">Sample Property Address</h1>
                <p class="section-title">Property Overview</p>
                <span class="page-number">Page 1</span>
              </div>
              <div class="header-right">
                <!-- Logo placeholder for component rendering -->
              </div>
            </div>
          </div>
          <div class="header-separator"></div>
          <div class="content-section">
            <h2 class="section-heading">Property Overview</h2>
            <div class="overview-grid">
              <div class="overview-item">
                <span class="overview-label">Total Area:</span>
                <span class="overview-value">150.00 m²</span>
              </div>
              <div class="overview-item">
                <span class="overview-label">HLP Length:</span>
                <span class="overview-value">25.50 m</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Main export function that matches your current export interface
   */
  static async exportToPDF(data: ExportData, filename: string = 'property-report.pdf', options: PuppeteerPDFOptions = {}): Promise<void> {
    try {
      console.log('🔄 Export started with options:', {
        hasLogo: !!options.logoDataUrl,
        logoDataLength: options.logoDataUrl?.length || 0
      });
      // Generate HTML from data
      const htmlContent = await this.generateHTMLFromData(data, options.logoDataUrl, options.headerColor);
      
      // Generate PDF with data
      await this.generatePDFFromHTML(htmlContent, filename, {}, data);
      
      console.log('✅ PDF generated successfully using Puppeteer-style rendering');
      
    } catch (error) {
      console.error('❌ Error exporting PDF:', error);
      throw error;
    }
  }

  /**
   * Capitalize floor names for better presentation
   */
  private static capitalizeFloorName(floorName: string): string {
    if (!floorName) return 'Floor';
    
    return floorName
      .split(' ')
      .map(word => {
        // Handle special cases
        if (word.toLowerCase() === 'floor') return 'Floor';
        if (word.toLowerCase() === 'ground') return 'Ground';
        if (word.toLowerCase() === 'main') return 'Main';
        if (word.toLowerCase() === 'building') return 'Building';
        if (word.toLowerCase() === 'extension') return 'Extension';
        if (word.toLowerCase() === 'conservatory') return 'Conservatory';
        if (word.toLowerCase() === 'basement') return 'Basement';
        if (word.toLowerCase() === 'attic') return 'Attic';
        if (word.toLowerCase() === 'loft') return 'Loft';
        
        // Handle ordinal numbers (1st, 2nd, 3rd, etc.)
        if (/^\d+(st|nd|rd|th)$/.test(word.toLowerCase())) {
          return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        }
        
        // Handle hyphens and special characters
        if (word.includes('-')) {
          return word.split('-').map(part => 
            part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
          ).join('-');
        }
        
        // Standard capitalization
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join(' ');
  }

  /**
   * Generate building fabric specifications table
   */
  private static generateBuildingFabricTable(siteNotesData: any): string {
    // Extract building fabric data from site notes
    const wallType = siteNotesData?.formData?.wallType || 'Not specified';
    const roofType = siteNotesData?.formData?.roofType || siteNotesData?.formData?.roofTypeRdSAP || 'Not specified';
    const floorType = siteNotesData?.formData?.floorType || 'Not specified';
    const wallThickness = siteNotesData?.formData?.wallThickness;
    
    // Format wall type with thickness if available
    const wallTypeDisplay = wallType + (wallThickness ? ` (${wallThickness}mm)` : '');

    return `
      <div class="building-fabric-container">
        <table class="building-fabric-table">
          <thead>
            <tr>
              <th>Wall Type</th>
              <th>Roof Type</th>
              <th>Floor Type</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>${wallTypeDisplay}</td>
              <td>${roofType}</td>
              <td>${floorType}</td>
            </tr>
          </tbody>
        </table>
      </div>
    `;
  }

  /**
   * Format frame material for proper capitalization
   */
  private static formatFrameMaterial(frameMaterial: string): string {
    if (!frameMaterial || frameMaterial === 'Not specified') return 'Not specified';
    
    // Handle common frame materials
    switch (frameMaterial.toLowerCase()) {
      case 'pvc':
        return 'PVC';
      case 'wood':
        return 'Wood';
      case 'metal':
        return 'Metal';
      case 'composite':
        return 'Composite';
      default:
        // Capitalize first letter for other materials
        return frameMaterial.charAt(0).toUpperCase() + frameMaterial.slice(1).toLowerCase();
    }
  }

  /**
   * Format orientation for proper capitalization
   */
  private static formatOrientation(orientation: string): string {
    if (!orientation || orientation === 'Not specified' || orientation === 'Unknown') return 'Not specified';
    
    // Handle compass directions
    switch (orientation.toLowerCase()) {
      case 'n':
      case 'north':
        return 'North';
      case 's':
      case 'south':
        return 'South';
      case 'e':
      case 'east':
        return 'East';
      case 'w':
      case 'west':
        return 'West';
      case 'ne':
      case 'northeast':
      case 'north-east':
        return 'Northeast';
      case 'nw':
      case 'northwest':
      case 'north-west':
        return 'Northwest';
      case 'se':
      case 'southeast':
      case 'south-east':
        return 'Southeast';
      case 'sw':
      case 'southwest':
      case 'south-west':
        return 'Southwest';
      default:
        // Capitalize first letter for other orientations
        return orientation.charAt(0).toUpperCase() + orientation.slice(1).toLowerCase();
    }
  }

  /**
   * Generate radiators and windows table for all rooms
   */
  private static generateRadiatorsWindowsTable(data: any, siteNotesData: any): string {
    if (!siteNotesData || !siteNotesData.floors || siteNotesData.floors.length === 0) {
      return '<div class="note-text">No radiator or window data available.</div>';
    }

    let tableRows = '';
    let totalRadiators = 0;
    let totalWindows = 0;

    siteNotesData.floors.forEach((floor: any) => {
      if (!floor.rooms || floor.rooms.length === 0) return;

      floor.rooms.forEach((room: any) => {
        const floorName = this.capitalizeFloorName(`${floor.floorLevel}${floor.buildingType !== 'main building' ? ` - ${floor.buildingType}` : ''}`);
        const roomName = room.name || `Room ${room.id}`;
        
        // Collect radiator data
        const radiators = [];
        
        // Single radiator (backward compatibility)
        if (room.radiatorWidth && room.radiatorHeight && room.radiatorType) {
          radiators.push({
            width: room.radiatorWidth,
            height: room.radiatorHeight,
            type: room.radiatorType
          });
        }
        
        // Additional radiators from array
        if (room.radiators && Array.isArray(room.radiators)) {
          room.radiators.forEach((rad: any) => {
            if (rad.width && rad.height && rad.type) {
              radiators.push({
                width: rad.width,
                height: rad.height,
                type: rad.type
              });
            }
          });
        }

        // 🛡️ SMART WINDOW COLLECTION: Avoid duplicates using prioritization logic
        const windows: any[] = [];
        
        let hasWindowData = false;
        
        // 🎯 SINGLE AUTHORITATIVE SOURCE: Prioritize windows array if it exists, otherwise use single window data
        // This prevents duplicates caused by auto-sync storing data in both places
        
        // Priority 1: Windows array data (most complete and allows multiple windows)
        if (room.windows && Array.isArray(room.windows) && room.windows.length > 0) {
          console.log(`🪟 PDF DEBUG: Room "${room.name || room.id}" using WINDOWS ARRAY as authoritative source (${room.windows.length} windows)`);
          
          room.windows.forEach((win: any, index: number) => {
            if (win.width && win.height) {
              windows.push({
                width: win.width,
                height: win.height,
                orientation: win.orientation || 'Not specified',
                trickleVents: win.trickleVents === true || win.trickleVents === 'true' || win.trickleVents === 'Yes',
                glazingType: win.glazingType || room.windowGlazingType || 'Not specified',
                glazingGap: win.glazingGap || room.windowGlazingGap || 'Not specified',
                frameMaterial: win.frameMaterial || win.windowType || win.type || room.windowType || 'Not specified',
                draughtProofed: (
                  win.isDraughtProofed === true ||
                  win.isDraughtProofed === 'true' ||
                  win.isDraughtProofed === 'Yes' ||
                  win.draughtProofed === true ||
                  win.draughtProofed === 'true' ||
                  win.draughtProofed === 'Yes' ||
                  room.windowDraughtProofed === true ||
                  room.windowDraughtProofed === 'true' ||
                  room.windowDraughtProofed === 'Yes'
                ),
                windowCategory: win.windowCategory || room.windowCategory || 'Not specified'
              });
              console.log(`🪟 PDF DEBUG: Added window ${index + 1} from array for room "${room.name || room.id}" (${win.width}x${win.height})`);
            }
          });
          
          hasWindowData = true;
        }
        // Priority 2: Single window data (fallback only if no windows array)
        else if (room.windowWidth && room.windowHeight) {
          console.log(`🪟 PDF DEBUG: Room "${room.name || room.id}" using SINGLE window data as fallback:`, {
            windowGlazingType: room.windowGlazingType,
            windowGlazingGap: room.windowGlazingGap,
            windowCategory: room.windowCategory,
            windowType: room.windowType,
            windowOrientation: room.windowOrientation,
            windowDraughtProofed: room.windowDraughtProofed,
            windowTrickleVents: room.windowTrickleVents
          });
          
          windows.push({
            width: room.windowWidth,
            height: room.windowHeight,
            orientation: room.windowOrientation || 'Not specified',
            trickleVents: room.windowTrickleVents === true || room.windowTrickleVents === 'true' || room.windowTrickleVents === 'Yes',
            glazingType: room.windowGlazingType || 'Not specified',
            glazingGap: room.windowGlazingGap || 'Not specified',
            frameMaterial: room.windowFrameMaterial || room.windowType || 'Not specified',
            draughtProofed: room.windowDraughtProofed === true || room.windowDraughtProofed === 'true' || room.windowDraughtProofed === 'Yes',
            windowCategory: room.windowCategory || 'Not specified'
          });
          
          hasWindowData = true;
          console.log(`🪟 PDF DEBUG: Added single window for room "${room.name || room.id}"`);
        }
        
        // Priority 3: Floor plan elements (last resort fallback only if no site notes data)
        if (!hasWindowData) {
          console.log(`🪟 PDF DEBUG: Room "${room.name || room.id}" using FLOOR PLAN ELEMENTS as last resort`);
          
          let floorPlanWindowsFound = 0;
          if (data.allFloorPlans) {
            Object.entries(data.allFloorPlans).forEach(([floorKey, floorData]: [string, any]) => {
              if (floorData && floorData.elements && Array.isArray(floorData.elements)) {
                floorData.elements.forEach((element: any) => {
                  if (element.type === 'window' && element.roomId === room.id) {
                    windows.push({
                      width: element.width || 'Not specified',
                      height: element.height || 'Not specified',
                      orientation: element.orientation || 'Not specified',
                      trickleVents: element.trickleVents === true || element.trickleVents === 'true' || element.trickleVents === 'Yes',
                      glazingType: element.glazingType || 'Not specified',
                      glazingGap: element.glazingGap || 'Not specified',
                      frameMaterial: element.windowType || 'Not specified',
                      draughtProofed: element.isDraughtProofed === true || element.isDraughtProofed === 'true' || element.isDraughtProofed === 'Yes',
                      windowCategory: element.windowCategory || 'Not specified'
                    });
                    floorPlanWindowsFound++;
                  }
                });
              }
            });
          }
          
          if (floorPlanWindowsFound > 0) {
            console.log(`🛡️ PDF DEDUP: Added ${floorPlanWindowsFound} windows from floor plan elements for room "${room.name || room.id}"`);
          } else {
            console.log(`🪟 PDF DEBUG: No window data found for room "${room.name || room.id}" from any source`);
          }
        }

        // Only create row if room has radiators or windows
        if (radiators.length > 0 || windows.length > 0) {
          totalRadiators += radiators.length;
          totalWindows += windows.length;

          // Format radiator info with clean presentation
          let radiatorInfo = '<span class="empty-cell">—</span>';
          if (radiators.length > 0) {
            if (radiators.length === 1) {
              radiatorInfo = `<div class="single-item">${radiators[0].width}×${radiators[0].height}<br><span class="item-type">${radiators[0].type}</span></div>`;
            } else {
              radiatorInfo = `<div class="multi-item-header">${radiators.length} radiators</div>` +
                radiators.map((r, index) => `<div class="item-row"><span class="row-num">${String.fromCharCode(65 + index)}</span><span class="item-spec">${r.width}×${r.height}</span><span class="item-desc">${r.type}</span></div>`).join('');
            }
          }

          // Create separate columns for each window property with clean design
          let windowDimensions = '<span class="empty-cell">—</span>';
          let windowGlazingType = '<span class="empty-cell">—</span>';
          let windowGlazingGap = '<span class="empty-cell">—</span>';
          let windowFrameMaterial = '<span class="empty-cell">—</span>';
          let windowDraughtProofed = '<span class="empty-cell">—</span>';
          let windowOrientation = '<span class="empty-cell">—</span>';

          if (windows.length > 0) {
            if (windows.length === 1) {
              windowDimensions = `<div class="single-item">${windows[0].width}×${windows[0].height}</div>`;
              windowGlazingType = `<div class="single-item">${this.abbreviateGlazingType(windows[0].glazingType)}</div>`;
              windowGlazingGap = `<div class="single-item">${this.abbreviateGap(windows[0].glazingGap)}</div>`;
              windowFrameMaterial = `<div class="single-item">${this.formatFrameMaterial(windows[0].frameMaterial)}</div>`;
              windowDraughtProofed = `<div class="single-item">${windows[0].draughtProofed ? 'Yes' : 'No'}</div>`;
              windowOrientation = `<div class="single-item">${this.formatOrientation(windows[0].orientation)}</div>`;
            } else {
              const header = `<div class="multi-item-header">${windows.length} windows</div>`;
              windowDimensions = header + windows.map((w, index) => `<div class="item-row"><span class="row-num">${String.fromCharCode(65 + index)}</span><span class="item-spec">${w.width}×${w.height}</span></div>`).join('');
              // 🔧 FIX: Fallback to room-level glazing data if window-level is empty or "Not specified"
              windowGlazingType = header + windows.map((w, index) => {
                const windowGlazingType = (w.glazingType && w.glazingType.trim() !== '' && w.glazingType !== 'Not specified') ? w.glazingType : '';
                const roomGlazingType = (room.windowGlazingType && room.windowGlazingType.trim() !== '' && room.windowGlazingType !== 'Not specified') ? room.windowGlazingType : '';
                const glazingType = windowGlazingType || roomGlazingType || 'Not specified';
                
                if (room.name === 'K') {
                  console.log(`🔧 PDF GLAZING DEBUG: Room "${room.name}", Window ${index + 1}: window="${windowGlazingType}", room="${roomGlazingType}", result="${glazingType}"`);
                }
                
                return `<div class="item-row"><span class="row-num">${String.fromCharCode(65 + index)}</span><span class="item-spec">${this.abbreviateGlazingType(glazingType)}</span></div>`;
              }).join('');
              windowGlazingGap = header + windows.map((w, index) => {
                const windowGlazingGap = (w.glazingGap && w.glazingGap.trim() !== '' && w.glazingGap !== 'Not specified') ? w.glazingGap : '';
                const roomGlazingGap = (room.windowGlazingGap && room.windowGlazingGap.trim() !== '' && room.windowGlazingGap !== 'Not specified') ? room.windowGlazingGap : '';
                const glazingGap = windowGlazingGap || roomGlazingGap || 'Not specified';
                
                if (room.name === 'K') {
                  console.log(`🔧 PDF GAP DEBUG: Room "${room.name}", Window ${index + 1}: window="${windowGlazingGap}", room="${roomGlazingGap}", result="${glazingGap}"`);
                }
                
                return `<div class="item-row"><span class="row-num">${String.fromCharCode(65 + index)}</span><span class="item-spec">${this.abbreviateGap(glazingGap)}</span></div>`;
              }).join('');
              windowFrameMaterial = header + windows.map((w, index) => `<div class="item-row"><span class="row-num">${String.fromCharCode(65 + index)}</span><span class="item-spec">${this.formatFrameMaterial(w.frameMaterial)}</span></div>`).join('');
              windowDraughtProofed = header + windows.map((w, index) => `<div class="item-row"><span class="row-num">${String.fromCharCode(65 + index)}</span><span class="item-spec">${w.draughtProofed ? 'Yes' : 'No'}</span></div>`).join('');
              windowOrientation = header + windows.map((w, index) => `<div class="item-row"><span class="row-num">${String.fromCharCode(65 + index)}</span><span class="item-spec">${this.formatOrientation(w.orientation)}</span></div>`).join('');
            }
          }

          tableRows += `
            <tr>
              <td class="floor-location">${floorName}</td>
              <td class="room-name">${roomName}</td>
              <td class="item-cell">${radiatorInfo}</td>
              <td class="item-cell">${windowDimensions}</td>
              <td class="item-cell">${windowGlazingType}</td>
              <td class="item-cell">${windowGlazingGap}</td>
              <td class="item-cell">${windowFrameMaterial}</td>
              <td class="item-cell">${windowDraughtProofed}</td>
              <td class="item-cell">${windowOrientation}</td>
        </tr>
      `;
        }
      });
    });

    if (!tableRows) {
      return '<div class="note-text">No radiator or window data found in any rooms.</div>';
    }

    return `
      <div class="clean-table-container">
        <table class="items-summary-table">
          <thead>
            <tr>
              <th class="col-floor">Floor</th>
              <th class="col-room">Room</th>
              <th class="col-radiator">Radiators</th>
              <th class="col-window">Window Size</th>
              <th class="col-glazing">Glazing Type</th>
              <th class="col-gap">Gap</th>
              <th class="col-frame">Frame</th>
              <th class="col-draught">Draught Proof</th>
              <th class="col-orient">Orientation</th>
            </tr>
          </thead>
          <tbody>
            ${tableRows}
          </tbody>
          <tfoot>
            <tr class="totals-row">
              <td colspan="2">Summary</td>
              <td>${totalRadiators} total</td>
              <td colspan="6">${totalWindows} windows across all rooms</td>
            </tr>
          </tfoot>
        </table>
      </div>
    `;
  }

    /**
   * Abbreviate glazing type for better table readability
   */
  private static abbreviateGlazingType(glazingType: string): string {
    if (!glazingType || glazingType === 'Not specified') return '—';
    
    const abbreviations: { [key: string]: string } = {
      'Single glazing': 'Single',
      'Double pre 2002': 'Double <2002',
      'Double between 2002 and 2021': 'Double 2002-21',
      'Double post or during 2022': 'Double ≥2022',
      'Double with unknown install date': 'Double (Unknown)',
      'Secondary glazing': 'Secondary',
      'Triple pre 2002': 'Triple <2002',
      'Triple between 2002 and 2021': 'Triple 2002-21',
      'Triple post or during 2022': 'Triple ≥2022',
      'Triple with unknown install date': 'Triple (Unknown)',
      'Single glazing, known data': 'Single (Known)',
      'Double glazing, known data': 'Double (Known)',
      'Triple glazing, known data': 'Triple (Known)',
      // Legacy options for backward compatibility
      'Double with known install date': 'Double (Known)',
      'Single glazed': 'Single',
      'Triple glazed': 'Triple',
      'Not specified': '—'
    };
    
    return abbreviations[glazingType] || glazingType.substring(0, 12) + (glazingType.length > 12 ? '...' : '');
  }

  /**
   * Abbreviate gap values for better table readability
   */
  private static abbreviateGap(gap: string): string {
    if (!gap || gap === 'Not specified') return '—';
    
    const abbreviations: { [key: string]: string } = {
      '16 mm or more': '≥16mm',
      '12 mm to 15 mm': '12-15mm',
      '6 mm to 11 mm': '6-11mm',
      'Less than 6 mm': '<6mm',
      'Not specified': '—'
    };
    
    return abbreviations[gap] || gap.replace(' mm', 'mm').replace(' or more', '+');
  }

  /**
   * Advanced text formatting function for modern UI
   */
  private static formatModernText(value: any): string {
    if (!value || value === '' || value === 'undefined' || value === 'null') return 'Not Specified';
    
    let text = String(value);
    
    // Handle special cases first
    if (text.toLowerCase() === 'not specified' || text.toLowerCase() === 'n/a' || text.toLowerCase() === 'na') {
      return 'Not Specified';
    }
    
    // Remove all types of dashes and underscores, normalize spacing
    text = text
      .replace(/[-_—–]/g, ' ')  // Replace all dash types and underscores
      .replace(/\s+/g, ' ')     // Normalize multiple spaces
      .trim();
    
    // Split into words and process each
    return text
      .split(' ')
      .map(word => {
        if (word.length === 0) return '';
        
        // Handle ordinals (1st, 2nd, 3rd, etc.)
        if (/^\d+(st|nd|rd|th)$/i.test(word)) {
          return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
        }
        
        // Handle numbers with units (225mm, 20°C, etc.)
        if (/^\d+[a-zA-Z°]+$/.test(word)) {
          return word;
        }
        
        // Handle acronyms (all uppercase words like MEV, TRV, etc.)
        if (word.length <= 4 && word.toUpperCase() === word && /^[A-Z]+$/.test(word)) {
          return word.toUpperCase();
        }
        
        // Handle words with internal hyphens (semi-detached, double-glazed)
        if (word.includes('-')) {
          return word.split('-').map(part => 
            part.charAt(0).toUpperCase() + part.slice(1).toLowerCase()
          ).join('-');
        }
        
        // Standard capitalization
        return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
      })
      .join(' ')
      .trim();
  }

  /**
   * Generate Wall Insulation Assessment sections for multi-page PDF (2 elevations per page)
   */
  private static generateWallInsulationSections(siteNotesData: any): string[] {
    const formData = siteNotesData?.formData || {};
    const elevations = formData.wallInsulationElevations || [];
    const globalCalcs = formData.wallInsulationGlobalCalculations || {};
    const floors = siteNotesData?.floors || [];
    
    // 🔧 FLOOR COUNT FIX: Only count main building floors, not extensions
    const mainBuildingFloors = floors.filter((f: any) => f.buildingType === 'main building');
    const actualFloorCount = mainBuildingFloors.length || 1;
    
    // DEBUG: Log wall insulation section generation
    console.log(`🏗️ WALL INSULATION EXPORT DEBUG - generateWallInsulationSections called:`, {
      hasFormData: !!formData,
      elevationsCount: elevations.length,
      wallInsulationType: formData.wallInsulationType,
      hasGlobalCalcs: !!globalCalcs,
      totalFloorsInData: floors.length,
      mainBuildingFloorsCount: actualFloorCount,
      extensionFloorsCount: floors.length - actualFloorCount
    });
    
    if (elevations.length === 0 && !formData.wallInsulationType) {
      console.log('⚠️ WALL INSULATION EXPORT DEBUG - No data available, returning no-data message');
      return ['<div class="no-data">No wall insulation data available</div>'];
    }

    // Extract property data for elevation diagrams
    const propertyData = {
      propertyType: formData.propertyType || 'house',
      roofTypeRdSAP: formData.roofTypeRdSAP || 'PA',
      groundLevel: formData.groundLevel || 'G',
      detachment: formData.detachment || 'detached',
      floorCount: actualFloorCount // Use main building floors only
    };

    // Split elevations into chunks of 2 for separate pages
    const elevationChunks = [];
    for (let i = 0; i < elevations.length; i += 2) {
      elevationChunks.push(elevations.slice(i, i + 2));
    }
    
    console.log(`📄 WALL INSULATION PAGINATION DEBUG - Split ${elevations.length} elevations into ${elevationChunks.length} pages (2 elevations per page)`);
    
    // Generate separate page content for each chunk
    return elevationChunks.map((chunk, pageIndex) => {
      console.log(`📄 WALL INSULATION PAGE ${pageIndex + 1} - Processing ${chunk.length} elevations:`, chunk.map((e: any) => e.type));
      const compactLayoutHTML = this.generateCompactWallInsulationLayout(formData, chunk, propertyData, globalCalcs, pageIndex === 0);
      return `<div class="wall-insulation-ultra-compact">${compactLayoutHTML}</div>`;
    });
  }

  /**
   * Generate single Wall Insulation Assessment section (backward compatibility)
   */
  private static generateWallInsulationSection(siteNotesData: any): string {
    const sections = this.generateWallInsulationSections(siteNotesData);
    return sections[0] || '';
  }

  /**
   * Generate separate PDF pages for Wall Insulation Assessment (2 elevations per page)
   */
  private static generateWallInsulationPages(siteNotesData: any, bestAddress: string, startingPageNumber: number, logoDataUrl?: string, totalPages?: number): string {
    const sections = this.generateWallInsulationSections(siteNotesData);
    
    return sections.map((sectionContent, pageIndex) => {
      const pageNumber = startingPageNumber + pageIndex;
      console.log(`📄 WALL INSULATION PDF PAGE ${pageNumber} - Creating page ${pageIndex + 1}/${sections.length}`);
      
      return `
        <div class="pdf-page">
          <div class="header">
            <div class="header-content">
              <div class="header-left">
                <h1 class="property-address">${bestAddress}</h1>
                <p class="section-title">Wall Insulation Assessment</p>
                <span class="page-number">Page ${pageNumber}${totalPages ? ` of ${totalPages}` : ''}</span>
              </div>
              <div class="header-right">
                ${logoDataUrl ? `<img src="${logoDataUrl}" alt="Company Logo" class="header-logo" />` : ''}
              </div>
            </div>
          </div>
          <div class="header-separator"></div>
          <div class="content-section">
            <h2 class="section-heading">Wall Insulation Assessment</h2>
            ${sectionContent}
          </div>
        </div>
        ${pageIndex < sections.length - 1 ? '<div class="page-break"></div>' : ''}
      `;
    }).join('');
  }

  /**
   * Calculate the next page number after wall insulation pages
   */
  private static calculateNextPageNumber(siteNotesData: any): string {
    const hasWallInsulation = siteNotesData?.formData?.wallInsulationType || 
      (siteNotesData?.formData?.wallInsulationElevations && siteNotesData.formData.wallInsulationElevations.length > 0);
    
    if (!hasWallInsulation) {
      return '3';
    }
    
    const elevations = siteNotesData?.formData?.wallInsulationElevations || [];
    const wallInsulationPages = Math.ceil(elevations.length / 2) || 1; // At least 1 page if wall insulation exists
    const nextPageNumber = 3 + wallInsulationPages;
    
    console.log(`📄 PAGE CALCULATION - Wall insulation elevations: ${elevations.length}, pages: ${wallInsulationPages}, next page: ${nextPageNumber}`);
    
    return nextPageNumber.toString();
  }

  /**
   * Calculate the final page number after wall insulation and RIR pages
   */
  private static calculateFinalPageNumber(siteNotesData: any): string {
    let pageNumber = parseInt(this.calculateNextPageNumber(siteNotesData));
    
    // Add 1 if RIR toggle is enabled (always show page regardless of measurements)
    const hasRoomInRoof = siteNotesData?.formData?.hasRoomInRoof === 'yes';
    if (hasRoomInRoof) {
      pageNumber += 1;
    }
    
    return pageNumber.toString();
  }

  /**
   * Generate Room in Roof diagram page
   */
  private static async generateRoomInRoofPage(siteNotesData: any, bestAddress: string, pageNumber: number, logoDataUrl?: string, totalPages?: number): Promise<string> {
    const rirData = siteNotesData?.formData?.roomInRoofData;
    const hasRoomInRoof = siteNotesData?.formData?.hasRoomInRoof === 'yes';
    
    // Only generate RIR page if toggle is enabled (diagram may not be captured on mobile)
    if (!hasRoomInRoof) {
      return '';
    }
    
    // If no measurements exist, still show page but with placeholder content
    const hasMeasurements = rirData?.measurements && rirData.measurements.length > 0;

    console.log('🎨 PUPPETEER RIR DEBUG:', {
      hasRoomInRoof,
      hasRirData: !!rirData,
      hasMeasurements,
      hasDiagramImage: !!rirData?.diagramImage,
      diagramImageLength: rirData?.diagramImage?.length,
      diagramImagePreview: rirData?.diagramImage?.substring(0, 50),
      diagramType: rirData?.diagramType,
      measurementsCount: rirData?.measurements?.length
    });

    const diagramTitle = `Room in Roof Diagram${rirData?.diagramType === 'with-dormer' ? ' with Dormer' : ''}`;
    
    // Get base SVG and convert to PNG
    const svgPath = rirData?.diagramType === 'with-dormer' 
      ? '/examples/rir diagram with dormer.svg'
      : '/examples/rir diagram.svg';
    const baseDiagramDataUrl = await fetchSVGAsPNG(svgPath, 800, 800);
    
    // Define ALL labels for both diagram types (always show all labels)
    const standardLabels = [
      { text: 'Party Wall Area', x: 485.8511627906977, y: 258.9395348837209, lineToX: 616.6790697674419, lineToY: 346.74418604651163 },
      { text: 'Flat Ceiling Area', x: 762.0093023255814, y: 320.4651162790698, lineToX: 579.2744186046511, lineToY: 397.9720930232558 },
      { text: 'Sloping Ceiling Area 1', x: 808.9767441860465, y: 383.6, lineToX: 661.5255813953488, lineToY: 421.7953488372093 },
      { text: 'Stud Wall Area 1', x: 901.693023255814, y: 468.0093023255814, lineToX: 638.5023255813953, lineToY: 497.5813953488372 },
      { text: 'Residual Area 1', x: 708.9860465116279, y: 577.1255813953488, lineToX: 632.1209302325582, lineToY: 545.7302325581395 },
      { text: 'Sloping Ceiling Area 2', x: 201.6, y: 355.7767441860465, lineToX: 321.86976744186046, lineToY: 516.9302325581396 },
      { text: 'Stud Wall Area 2', x: 120, y: 510, lineToX: 280.5581395348837, lineToY: 540.4 },
      { text: 'Residual Area 2', x: 120, y: 570, lineToX: 264.6418604651163, lineToY: 614.4186046511628 },
      { text: 'RIR Floor Area', x: 380, y: 680, lineToX: 415.6744186046512, lineToY: 629.4604651162791 },
      { text: 'Stud Wall 2', x: 200, y: 750, lineToX: 303.7860465116279, lineToY: 591.6837209302325 },
      { text: 'Stud Wall 1', x: 600, y: 750, lineToX: 492.89302325581394, lineToY: 600.353488372093 },
      { text: 'Gable Wall Area', x: 484.0558139534884, y: 778.7720930232558, lineToX: 425.8139534883721, lineToY: 576.046511627907 },
    ];

    const dormerLabels = [
      { text: 'Party Wall Area', x: 320, y: 320, lineToX: 418.4372093023256, lineToY: 479.27441860465115 },
      { text: 'Flat Ceiling Area', x: 528.5674418604651, y: 407.69302325581396, lineToX: 319.50697674418603, lineToY: 576.4651162790698 },
      { text: 'Sloping Ceiling Area 1', x: 514, y: 446.0186046511628, lineToX: 453.51627906976745, lineToY: 555.6279069767442 },
      { text: 'Stud Wall Area 1', x: 536.5581395348837, y: 515.4325581395349, lineToX: 487.86046511627904, lineToY: 600.5488372093023 },
      { text: 'Residual Area 1', x: 573.9162790697675, y: 586.2976744186046, lineToX: 474.3720930232558, lineToY: 654.3255813953489 },
      { text: 'Sloping Ceiling Area 2', x: 167.5906976744186, y: 389.3860465116279, lineToX: 163.62790697674419, lineToY: 629.5441860465116 },
      { text: 'Stud Wall Area 2', x: 120, y: 510, lineToX: 139.6, lineToY: 654.0930232558139 },
      { text: 'Residual Area 2', x: 120, y: 570, lineToX: 81.45116279069768, lineToY: 743.9255813953488 },
      { text: 'RIR Floor Area', x: 525.0883720930233, y: 856.0837209302325, lineToX: 230.44651162790697, lineToY: 722.8651162790698 },
      { text: 'Stud Wall 2', x: 87.61860465116278, y: 833.4697674418604, lineToX: 111.12558139534883, lineToY: 756.2046511627907 },
      { text: 'Stud Wall 1', x: 514.5767441860465, y: 730.1023255813953, lineToX: 301.36744186046514, lineToY: 730.0093023255814 },
      { text: 'Gable Wall Area', x: 519.7581395348838, y: 924.9116279069767, lineToX: 165.69302325581396, lineToY: 736.5674418604651 },
      { text: 'Cheek Area 1', x: 547.9348837209302, y: 300.3255813953488, lineToX: 734.7255813953489, lineToY: 361.10697674418606 },
      { text: 'Cheek Area 2', x: 896.7441860465116, y: 277.0046511627907, lineToX: 835.953488372093, lineToY: 317.86976744186046 },
      { text: 'Dormer Ceiling Area', x: 823.2558139534884, y: 454.1674418604651, lineToX: 788.4, lineToY: 331.5906976744186 },
    ];
    
    const labels = rirData?.diagramType === 'with-dormer' ? dormerLabels : standardLabels;
    
    // Generate SVG arrows and labels
    const labelsHTML = labels.map(label => `
      <line x1="${label.x}" y1="${label.y}" x2="${label.lineToX}" y2="${label.lineToY}" stroke="#1F2937" stroke-width="2" marker-end="url(#arrowhead)" />
      <text x="${label.x}" y="${label.y - 5}" font-size="14" fill="#1F2937" font-weight="600" text-anchor="${label.x > 512 ? 'start' : 'end'}">${label.text}</text>
    `).join('');
    
    console.log('📐 RIR DIAGRAM: Rendering with all labels', {
      diagramType: rirData?.diagramType,
      labelCount: labels.length,
      hasBaseImage: !!baseDiagramDataUrl
    });
    
    // Generate table rows for measurements (or show placeholder if none)
    const tableRows = hasMeasurements
      ? rirData.measurements
          .filter((m: any) => m.visible !== false)
          .map((m: any) => `
            <tr>
              <td>${m.label}</td>
              <td>${m.length || '-'}</td>
              <td>${m.height || '-'}</td>
              <td>${m.insulation || '-'}</td>
              <td>${m.insulationType || '-'}</td>
            </tr>
          `).join('')
      : `<tr><td colspan="5" style="text-align: center; color: #64748B; padding: 16pt;">No measurements recorded yet</td></tr>`;


    return `
      <div class="pdf-page">
        <div class="header">
          <div class="header-content">
            <div class="header-left">
              <h1 class="property-address">${bestAddress}</h1>
              <p class="section-title">Room in Roof Assessment</p>
              <span class="page-number">Page ${pageNumber}${totalPages ? ` of ${totalPages}` : ''}</span>
            </div>
            <div class="header-right">
              ${logoDataUrl ? `<img src="${logoDataUrl}" alt="Company Logo" class="header-logo" />` : ''}
            </div>
          </div>
        </div>
        <div class="header-separator"></div>
        <div class="content-section">
          <h2 class="section-heading">${diagramTitle}</h2>
          
          <!-- Diagram Visualization with arrows and labels -->
          ${baseDiagramDataUrl ? `
            <div style="margin: 16pt 0; text-align: center; page-break-inside: avoid; position: relative;">
              <div style="position: relative; display: inline-block; max-width: 100%;">
                <img src="${baseDiagramDataUrl}" style="max-width: 100%; max-height: 400pt; display: block; border: 1pt solid #E2E8F0; border-radius: 4pt; background: white;" />
                <svg style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; pointer-events: none;" viewBox="0 0 1024 1024" preserveAspectRatio="xMidYMid meet">
                  <defs>
                    <marker id="arrowhead" markerWidth="10" markerHeight="10" refX="9" refY="3" orient="auto">
                      <polygon points="0 0, 10 3, 0 6" fill="#1F2937" />
                    </marker>
                  </defs>
                  ${labelsHTML}
                </svg>
              </div>
              <p style="font-size: 6pt; color: #64748b; margin-top: 2pt;">RIR Insulation Diagram - ${rirData.diagramType === 'with-dormer' ? 'With Dormer' : 'Standard'}</p>
            </div>
          ` : `
            <div style="text-align: center; padding: 40pt 20pt; background: #F8FAFC; border: 2pt dashed #CBD5E1; border-radius: 8pt; margin: 16pt auto; max-width: 600px;">
              <div style="font-size: 24pt; margin-bottom: 8pt;">📐</div>
              <div style="font-size: 10pt; color: #64748B; font-weight: 600;">RIR Diagram Unavailable</div>
            </div>
          `}
          
          <div class="rir-diagram-section">
            <h3>Area Details</h3>
            <table class="rir-table">
              <thead>
                <tr>
                  <th>Area</th>
                  <th>Length (m)</th>
                  <th>Height (m)</th>
                  <th>Insulation</th>
                  <th>Insulation Type</th>
                </tr>
              </thead>
              <tbody>
                ${tableRows}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Generate ultra-compact wall insulation layout with integrated diagrams and data
   */
  private static generateCompactWallInsulationLayout(formData: any, elevations: any[], propertyData: any, globalCalcs: any, includeHeader: boolean = true): string {
    const insulationType = this.formatModernText(formData.wallInsulationType || 'Not specified');
    const propertyType = this.formatModernText(formData.propertyType || 'Not specified');
    const elevationsCount = elevations.length || 0;
    
    // Property summary data
    const propertySummary = [
      ['Type', propertyType],
      ['Insulation', insulationType],
      ['Elevations', elevationsCount.toString()]
    ];
    
    // Global calculations data
    const totalDwellingArea = globalCalcs.totalDwellingArea ? `${globalCalcs.totalDwellingArea.toFixed(1)}m²` : 'N/A';
    const totalUntreatableArea = globalCalcs.totalUntreatableArea ? `${globalCalcs.totalUntreatableArea.toFixed(1)}m²` : 'N/A';
    const totalTreatableArea = globalCalcs.totalTreatableArea ? `${globalCalcs.totalTreatableArea.toFixed(1)}m²` : 'N/A';
    const poptPercentage = globalCalcs.poptPercentage ? `${globalCalcs.poptPercentage.toFixed(1)}%` : 'N/A';
    
    const globalData = [
      ['Total Area', totalDwellingArea],
      ['Untreatable', totalUntreatableArea, 'red'],
      ['Treatable', totalTreatableArea, 'green'],
      ['POPT %', poptPercentage, 'blue']
    ];
    
    // Generate elevation cards with integrated diagrams (no CSS page breaks - handled by manual pagination)
    const elevationCards = elevations.map((elevation, index) => {
      const elevationType = this.formatModernText(elevation.type || 'Unknown');
      const calculations = elevation.calculations || {};
      const elements = elevation.elements || {};
      
      const wallArea = calculations.totalWallArea ? calculations.totalWallArea.toFixed(1) : '0.0';
      const treatableArea = calculations.treatableArea ? calculations.treatableArea.toFixed(1) : '0.0';
      const doorsCount = elements.doors?.length || 0;
      const windowsCount = elements.windows?.length || 0;
      const ventsCount = elements.airVents?.length || 0;
      
      // Generate diagram SVG
      const diagramSVG = this.generatePropertyElevationSVG(elevation, propertyData, formData.wallInsulationType);
      
      // Generate element details table
      const elementsTable = this.generateCompactElementsTable(elevation);
      
      console.log(`📄 WALL INSULATION PAGE CONTENT - Elevation ${index + 1}: ${elevationType}`);
      
      return `
        <div class="elevation-compact-card">
          <div class="elevation-header-compact">
            <span class="elevation-type">${elevationType}</span>
            <div class="elevation-metrics-inline">
              <span class="metric wall-area">${wallArea}m²</span>
              <span class="metric treatable" data-type="green">${treatableArea}m²</span>
              <div class="elements-inline">
                ${doorsCount > 0 ? `<span class="el doors">${doorsCount}D</span>` : ''}
                ${windowsCount > 0 ? `<span class="el windows">${windowsCount}W</span>` : ''}
                ${ventsCount > 0 ? `<span class="el vents">${ventsCount}V</span>` : ''}
              </div>
            </div>
          </div>
          <div class="elevation-content-compact">
            <div class="diagram-compact">${diagramSVG}</div>
            <div class="elements-data-compact">${elementsTable}</div>
          </div>
        </div>
      `;
    }).join('');
    
    return `
      <div class="wall-assessment-compact">
        ${includeHeader ? `
        <div class="assessment-header">
          <h3>Wall Insulation Assessment</h3>
        </div>
        <div class="assessment-grid">
          <div class="property-summary">
            <h4>Property</h4>
            <div class="data-grid">
              ${propertySummary.map(([label, value]) => `<div class="data-row"><span class="label">${label}</span><span class="value">${value}</span></div>`).join('')}
            </div>
          </div>
          <div class="global-calculations">
            <h4>Calculations</h4>
            <div class="data-grid">
              ${globalData.map(([label, value, type]) => `<div class="data-row"><span class="label">${label}</span><span class="value" ${type ? `data-type="${type}"` : ''}>${value}</span></div>`).join('')}
            </div>
          </div>
        </div>
        ` : ''}
        <div class="elevations-grid">
          ${elevationCards}
        </div>
      </div>
    `;
  }
  
  /**
   * Generate compact elements table for elevation
   */
  private static generateCompactElementsTable(elevation: any): string {
    const elements = elevation.elements || {};
    const placedElements = elevation.placedElements || [];
    
    let allElements: any[] = [];
    
    // Collect all elements
    if (elements.doors) {
      allElements = allElements.concat(elements.doors.map((el: any, i: number) => ({...el, type: 'door', id: `D${i+1}`})));
    }
    if (elements.windows) {
      allElements = allElements.concat(elements.windows.map((el: any, i: number) => ({...el, type: 'window', id: `W${i+1}`})));
    }
    if (elements.airVents) {
      allElements = allElements.concat(elements.airVents.map((el: any, i: number) => ({...el, type: 'vent', id: `V${i+1}`})));
    }
    
    if (allElements.length === 0) {
      return '<div class="no-elements-compact">No elements</div>';
    }
    
    const elementRows = allElements.map(element => {
      const isMeters = element.height && element.height < 10;
      const height = element.height ? 
                   (isMeters ? element.height.toFixed(2) : (element.height / 1000).toFixed(2)) : 
                   (element.type === 'door' ? '2.00' : element.type === 'vent' ? 'N/A' : '1.20');
      const width = element.width ? 
                   (isMeters ? element.width.toFixed(2) : (element.width / 1000).toFixed(2)) : 
                   (element.type === 'door' ? '0.89' : element.type === 'vent' ? 'N/A' : '1.20');
      const area = element.type === 'vent' ? 'N/A' : 
                  element.height && element.width ? 
                  (isMeters ? (element.height * element.width).toFixed(2) : ((element.height * element.width) / 1000000).toFixed(2)) : 
                  (parseFloat(height) * parseFloat(width)).toFixed(2);
      
      return `<tr class="${element.type}-row"><td class="id">${element.id}</td><td>${height}</td><td>${width}</td><td>${area}</td></tr>`;
    }).join('');
    
    return `
      <table class="elements-compact-table">
        <thead><tr><th>ID</th><th>H</th><th>W</th><th>Area</th></tr></thead>
        <tbody>${elementRows}</tbody>
      </table>
    `;
  }
  
  /**
   * Generate combined wall insulation overview and details section (LEGACY - KEEPING FOR COMPATIBILITY)
   */
  private static generateWallInsulationOverview(formData: any): string {
    const insulationType = this.formatModernText(formData.wallInsulationType || 'Not specified');
    const propertyType = this.formatModernText(formData.propertyType || 'Not specified');
    const elevationsCount = formData.wallInsulationElevationsCount || '0';
    const elevations = formData.wallInsulationElevations || [];
    
    // Get global calculations data
    const globalCalc = formData.wallInsulationGlobalCalculations || {};
    const totalDwellingArea = globalCalc.totalDwellingArea ? `${globalCalc.totalDwellingArea.toFixed(2)}m²` : 'N/A';
    const totalUntreatableArea = globalCalc.totalUntreatableArea ? `${globalCalc.totalUntreatableArea.toFixed(2)}m²` : 'N/A';
    const totalTreatableArea = globalCalc.totalTreatableArea ? `${globalCalc.totalTreatableArea.toFixed(2)}m²` : 'N/A';
    const poptPercentage = globalCalc.poptPercentage ? `${globalCalc.poptPercentage.toFixed(1)}%` : 'N/A';

    // Generate elevation details rows
    const elevationRows = elevations.map((elevation: any) => {
      const elevationType = this.formatModernText(elevation.type || 'Unknown');
      const dimensions = elevation.dimensions || {};
      const calculations = elevation.calculations || {};
      const elements = elevation.elements || {};
      
      const wallArea = calculations.totalWallArea ? `${calculations.totalWallArea.toFixed(2)} m²` : 'N/A';
      const treatableArea = calculations.treatableArea ? `${calculations.treatableArea.toFixed(2)} m²` : 'N/A';
      const doorsCount = elements.doors?.length || 0;
      const windowsCount = elements.windows?.length || 0;
      const ventsCount = elements.airVents?.length || 0;

      return `
        <tr class="elevation-detail-row">
          <td class="elevation-label">${elevationType} Elevation</td>
          <td class="elevation-data">
            <div class="elevation-metrics">
              <div class="metric-group">
                <span class="metric-label">Wall Area:</span>
                <span class="metric-value">${wallArea}</span>
              </div>
              <div class="metric-group">
                <span class="metric-label">Treatable:</span>
                <span class="metric-value green">${treatableArea}</span>
              </div>
              <div class="elements-badges">
                ${doorsCount > 0 ? `<span class="badge doors">${doorsCount}D</span>` : ''}
                ${windowsCount > 0 ? `<span class="badge windows">${windowsCount}W</span>` : ''}
                ${ventsCount > 0 ? `<span class="badge vents">${ventsCount}V</span>` : ''}
                ${doorsCount === 0 && windowsCount === 0 && ventsCount === 0 ? '<span class="no-elements">No elements</span>' : ''}
              </div>
            </div>
          </td>
        </tr>
      `;
    }).join('');

    return `
      <div class="wall-insulation-overview-enhanced">
        <div class="modern-section-header">
          <h3 class="modern-section-title">Insulation Assessment & Property Analysis</h3>
        </div>
        <div class="enhanced-content">
          <table class="modern-insulation-table">
            <thead>
              <tr class="table-header">
                <th class="header-property">Property Information</th>
                <th class="header-calculations">Property-Wide Calculations</th>
              </tr>
            </thead>
            <tbody>
              <tr class="overview-main-row">
                <td class="property-info">
                  <div class="info-grid">
                    <div class="info-item">
                      <span class="info-label">Property Type</span>
                      <span class="info-value">${propertyType}</span>
                    </div>
                    <div class="info-item">
                      <span class="info-label">Insulation Type</span>
                      <span class="info-value">${insulationType}</span>
                    </div>
                    <div class="info-item">
                      <span class="info-label">Elevations Assessed</span>
                      <span class="info-value">${elevationsCount}</span>
                    </div>
                  </div>
                </td>
                <td class="calculations-info">
                  <div class="calc-grid">
                    <div class="calc-item">
                      <span class="calc-label">Total Dwelling Area</span>
                      <span class="calc-value">${totalDwellingArea}</span>
                    </div>
                    <div class="calc-item">
                      <span class="calc-label">Untreatable Area</span>
                      <span class="calc-value red">${totalUntreatableArea}</span>
                    </div>
                    <div class="calc-item">
                      <span class="calc-label">Treatable Area</span>
                      <span class="calc-value green">${totalTreatableArea}</span>
                    </div>
                    <div class="calc-item">
                      <span class="calc-label">POPT (Treatability)</span>
                      <span class="calc-value blue">${poptPercentage}</span>
                    </div>
                  </div>
                </td>
              </tr>
              ${elevationRows}
            </tbody>
          </table>
        </div>
      </div>
    `;
  }

  /**
   * Generate wall insulation elevations table and diagrams
   */
  private static generateWallInsulationElevationsTable(elevations: any[], propertyData?: any, wallInsulationType?: string): string {
    if (elevations.length === 0) {
      return `
        <div class="wall-insulation-elevations">
          <div class="section-header insulation-header">
            <h3 class="section-title">Elevation Details</h3>
          </div>
          <div class="no-data">No elevation data available</div>
        </div>
      `;
    }

    const elevationRows = elevations.map((elevation: any) => {
      const elevationType = this.formatModernText(elevation.type || 'Unknown');
      const dimensions = elevation.dimensions || {};
      const calculations = elevation.calculations || {};
      const elements = elevation.elements || {};
      
      const wallArea = calculations.totalWallArea ? `${calculations.totalWallArea.toFixed(2)} m²` : 'N/A';
      const treatableArea = calculations.treatableArea ? `${calculations.treatableArea.toFixed(2)} m²` : 'N/A';
      const doorsCount = elements.doors?.length || 0;
      const windowsCount = elements.windows?.length || 0;
      const ventsCount = elements.airVents?.length || 0;

      return `
        <tr>
          <td class="elevation-type">${elevationType}</td>
          <td class="elevation-area">${wallArea}</td>
          <td class="elevation-treatable">${treatableArea}</td>
          <td class="elevation-elements">
            <div class="elements-summary">
              ${doorsCount > 0 ? `<span class="element-count doors">${doorsCount} Door${doorsCount > 1 ? 's' : ''}</span>` : ''}
              ${windowsCount > 0 ? `<span class="element-count windows">${windowsCount} Window${windowsCount > 1 ? 's' : ''}</span>` : ''}
              ${ventsCount > 0 ? `<span class="element-count vents">${ventsCount} Vent${ventsCount > 1 ? 's' : ''}</span>` : ''}
              ${doorsCount === 0 && windowsCount === 0 && ventsCount === 0 ? '<span class="no-elements">No elements</span>' : ''}
            </div>
          </td>
        </tr>
      `;
    }).join('');

    // Generate property elevation diagrams
    const elevationDiagrams = this.generatePropertyElevationDiagrams(elevations, propertyData, wallInsulationType);

    return `
      <div class="wall-insulation-elevations">
        <div class="section-header insulation-header">
          <h3 class="section-title">Elevation Details</h3>
        </div>
        <div class="elevations-content">
          <table class="elevations-table">
            <thead>
              <tr>
                <th>Elevation</th>
                <th>Wall Area</th>
                <th>Treatable Area</th>
                <th>Elements</th>
              </tr>
            </thead>
            <tbody>
              ${elevationRows}
            </tbody>
          </table>
        </div>
      </div>
      ${elevationDiagrams}
    `;
  }

  /**
   * Generate property elevation diagrams for all elevations
   */
  private static generatePropertyElevationDiagrams(elevations: any[], propertyData?: any, wallInsulationType?: string): string {
    if (elevations.length === 0) return '';

    const diagrams = elevations.map((elevation: any) => {
      if (!elevation.type || !elevation.dimensions) return '';
      
      const elevationType = this.formatModernText(elevation.type);
      const diagramSVG = this.generatePropertyElevationSVG(elevation, propertyData, wallInsulationType);
      
      // Generate elevation details table
      const elevationDetailsHTML = this.generateElevationDetailsTable(elevation);
      
      return `
        <div class="property-elevation-diagram">
          <div class="section-header insulation-header">
            <h3 class="section-title">${elevationType} Elevation</h3>
          </div>
          <div class="elevation-content-grid">
            <div class="elevation-diagram-content">
              ${diagramSVG}
            </div>
            <div class="elevation-details-content">
              ${elevationDetailsHTML}
            </div>
          </div>
        </div>
      `;
    }).join('');

    return `
      <div class="property-elevations-container">
        ${diagrams}
      </div>
    `;
  }

  /**
   * Generate elevation details table with dimensions and element breakdown
   */
  private static generateElevationDetailsTable(elevation: any): string {
    const dimensions = elevation.dimensions || {};
    const calculations = elevation.calculations || {};
    const elements = elevation.elements || {};
    const placedElements = elevation.placedElements || [];
    
    // Elevation overview details
    const height = dimensions.height ? `${dimensions.height}m` : 'N/A';
    const width = dimensions.width ? `${dimensions.width}m` : 'N/A';
    const wallArea = calculations.totalWallArea ? `${calculations.totalWallArea.toFixed(2)}m²` : 'N/A';
    const untreatableArea = calculations.totalElementsArea ? `${calculations.totalElementsArea.toFixed(2)}m²` : 'N/A';
    const treatableArea = calculations.treatableArea ? `${calculations.treatableArea.toFixed(2)}m²` : 'N/A';

    // Generate individual element tables
    const doorsTableHTML = this.generateElementDetailsTable(placedElements, elements, 'door', 'D');
    const windowsTableHTML = this.generateElementDetailsTable(placedElements, elements, 'window', 'W');
    const ventsTableHTML = this.generateElementDetailsTable(placedElements, elements, 'airvent', 'V');

    return `
      <div class="elevation-details">
        <!-- Elevation Overview -->
        <table class="elevation-overview-table">
          <tbody>
            <tr>
              <td class="detail-label">Height</td>
              <td class="detail-value">${height}</td>
            </tr>
            <tr>
              <td class="detail-label">Width</td>
              <td class="detail-value">${width}</td>
            </tr>
            <tr>
              <td class="detail-label">Wall Area</td>
              <td class="detail-value">${wallArea}</td>
            </tr>
          </tbody>
        </table>

        <!-- Calculation Results -->
        <div class="calculation-results">
          <h4 class="calculation-header">Calculation</h4>
          <table class="calculation-table">
            <tbody>
              <tr>
                <td class="calc-label">Untreatable Area</td>
                <td class="calc-value">${untreatableArea}</td>
              </tr>
              <tr>
                <td class="calc-label">Treatable Area</td>
                <td class="calc-value">${treatableArea}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Element Details -->
        ${doorsTableHTML}
        ${windowsTableHTML}
        ${ventsTableHTML}
      </div>
    `;
  }

  /**
   * Generate individual element details table (doors, windows, vents)
   */
  private static generateElementDetailsTable(placedElements: any[], elements: any, elementType: string, idPrefix: string): string {
    let elementsList: any[] = [];
    
    // Priority 1: Use elements data (like jsPDF) - this has proper dimensions
    if (elements && elements[elementType + 's']) {
      elementsList = elements[elementType + 's'] || [];
    } else if (placedElements && placedElements.length > 0) {
      // Fallback: Get elements from placed elements  
      elementsList = placedElements.filter(el => el.type === elementType);
    }

    if (elementsList.length === 0) return '';

    const elementTypeDisplayName = elementType === 'airvent' ? 'Air Vents' : 
                                   elementType === 'door' ? 'Doors' : 'Windows';

    let tableContent = '';
    
    if (elementType === 'airvent') {
      // Air vents table format
      tableContent = elementsList.map((element: any, index: number) => `
        <tr>
          <td class="element-id">${idPrefix}${index + 1}</td>
          <td class="element-data">Air Vent</td>
          <td class="element-data">Standard</td>
          <td class="element-data">N/A</td>
        </tr>
      `).join('');
      
      return `
        <div class="element-details-section">
          <table class="element-details-table vents-table">
            <thead>
              <tr>
                <th class="element-header">ID</th>
                <th class="element-header">Type</th>
                <th class="element-header">Size</th>
                <th class="element-header">Notes</th>
              </tr>
            </thead>
            <tbody>
              ${tableContent}
            </tbody>
          </table>
        </div>
      `;
          } else {
      // Doors and windows table format
      tableContent = elementsList.map((element: any, index: number) => {
        // Check if dimensions are already in meters (from elements data) or need conversion (from placedElements)
        const isMeters = element.height && element.height < 10; // Assume values < 10 are in meters
        
        const height = element.height ? 
                      (isMeters ? element.height.toFixed(2) : (element.height / 1000).toFixed(2)) : 
                      (elementType === 'door' ? '2.00' : '1.20');
        const width = element.width ? 
                     (isMeters ? element.width.toFixed(2) : (element.width / 1000).toFixed(2)) : 
                     (elementType === 'door' ? '0.89' : '1.20');
        const area = element.height && element.width ? 
                    (isMeters ? (element.height * element.width).toFixed(2) : ((element.height * element.width) / 1000000).toFixed(2)) : 
                    (parseFloat(height) * parseFloat(width)).toFixed(2);
        
        return `
          <tr>
            <td class="element-id">${idPrefix}${index + 1}</td>
            <td class="element-data">${height}</td>
            <td class="element-data">${width}</td>
            <td class="element-data">${area}</td>
          </tr>
        `;
      }).join('');
      
      return `
        <div class="element-details-section">
          <table class="element-details-table ${elementType}s-table">
            <thead>
              <tr>
                <th class="element-header">ID</th>
                <th class="element-header">Height (m)</th>
                <th class="element-header">Width (m)</th>
                <th class="element-header">Area (m²)</th>
              </tr>
            </thead>
            <tbody>
              ${tableContent}
            </tbody>
          </table>
        </div>
      `;
    }
  }

  /**
   * Generate property elevation SVG diagram - EXACT CARBON COPY of jsPDF implementation
   */
  private static generatePropertyElevationSVG(elevation: any, propertyData?: any, wallInsulationType?: string): string {
    const elevationType = elevation.type;
    const elevationData = elevation;
    
    // EXACT CARBON COPY: Replicate the exact same logic from jsPDF exportUtils.ts
    const getBuildingHeight = () => {
      if (propertyData?.propertyType === 'bungalow') return 1;
      const floorCount = propertyData?.floorCount || 1;
      return Math.max(floorCount, 1);
    };

    const hasRoof = () => {
      // No roof if it's flat (F) or has dwelling/space above (S, A, NR)
      return !['F', 'S', 'A', 'NR'].includes(propertyData?.roofTypeRdSAP || 'PA');
    };

    const getRoofStyle = () => {
      if (!hasRoof()) return 'flat';
      if (propertyData?.roofTypeRdSAP === 'PT') return 'thatch';
      if (propertyData?.roofTypeRdSAP === 'PS') return 'sloping';
      return 'pitched'; // PA, PN
    };

    const hasBasement = () => {
      return ['P', 'U', 'E'].includes(propertyData?.groundLevel || 'G');
    };

    // Use the same property characteristics as the interactive diagram
    const propertyType = propertyData?.propertyType || 'house';
    const buildingHeight = getBuildingHeight();
    const showRoof = hasRoof();
    const roofStyle = getRoofStyle();
    const showBasement = hasBasement();
    const elevationWallInsulationType = elevationData.wallInsulationType || wallInsulationType || 'cavity';
    
    // EXACT CARBON COPY: Replicate exact getAdaptiveDimensions logic from PropertyElevationDiagram
    let dimensions;
    const isSingleStory = buildingHeight === 1;
    
    if (isSingleStory) {
      // Single story: closer/fuller view with larger story height (EXACT match to interactive component)
      dimensions = {
        svgWidth: 500,
        svgHeight: 420,  // ✅ Fixed: was 400, now matches interactive
        viewBoxWidth: 500,
        viewBoxHeight: 420,  // ✅ Fixed: was 400, now matches interactive
        storyHeight: 204,  // ✅ Fixed: was 136, now matches interactive (120 * 1.7 = 204)
        buildingWidth: 350,
        buildingX: 75,
        groundY: 370
      };
          } else {
      // Multi-story: adjust to fit everything including roof (EXACT match to interactive component)
      const baseStoryHeight = buildingHeight <= 2 ? 136 : Math.max(102, 238 / buildingHeight); // 80 * 1.7 = 136, 60 * 1.7 = 102, 140 * 1.7 = 238
      const totalRequiredHeight = (buildingHeight * baseStoryHeight) + (showRoof ? 80 : 0) + (showBasement ? 40 : 0) + 80; // 80 for margins
      const adaptiveHeight = Math.max(450, totalRequiredHeight);
      
      dimensions = {
        svgWidth: 500,
        svgHeight: adaptiveHeight,
        viewBoxWidth: 500,
        viewBoxHeight: adaptiveHeight,
        storyHeight: baseStoryHeight,
        buildingWidth: 350,
        buildingX: 75,
        groundY: adaptiveHeight - 80 // Keep some margin from bottom
      };
    }

    const { storyHeight, buildingWidth, buildingX, groundY, svgWidth, svgHeight, viewBoxWidth, viewBoxHeight } = dimensions;
    const basementHeight = showBasement ? 40 : 0;
    const buildingY = groundY - (buildingHeight * storyHeight) - basementHeight;
    const dpcHeight = 20;

    // EXACT CARBON COPY: Generate SVG string identical to jsPDF implementation
    let svgContent = `
      <svg width="${svgWidth}" height="${svgHeight}" viewBox="0 0 ${viewBoxWidth} ${viewBoxHeight}" xmlns="http://www.w3.org/2000/svg">
        <!-- Ground -->
        <line x1="0" y1="${groundY}" x2="${viewBoxWidth}" y2="${groundY}" stroke="black" stroke-width="2"/>
        
        <!-- Basement/Foundation if applicable -->
        ${showBasement ? `
        <rect x="${buildingX}" y="${groundY - basementHeight}" width="${buildingWidth}" height="${basementHeight}" 
              fill="white" stroke="black" stroke-width="1"/>
        ` : ''}
        
        <!-- Main building structure -->
        <rect x="${buildingX}" y="${buildingY}" width="${buildingWidth}" height="${buildingHeight * storyHeight}" 
              fill="white" stroke="black" stroke-width="2"/>
        
        <!-- DPC Section -->
        <rect x="${buildingX}" y="${buildingY + (buildingHeight * storyHeight) - dpcHeight}" 
              width="${buildingWidth}" height="${dpcHeight}" fill="#f5f5f5" stroke="black" stroke-width="1"/>
    `;

    // Add cavity brickwork pattern if cavity insulation
    if (elevationWallInsulationType === 'cavity') {
      svgContent += this.generateCavityBrickworkSVGExact(buildingX, buildingY, buildingWidth, (buildingHeight * storyHeight) - dpcHeight);
    }

    // Add floor division lines for multi-storey
    for (let i = 0; i < buildingHeight - 1; i++) {
      const lineY = buildingY + ((i + 1) * storyHeight);
      svgContent += `<line x1="${buildingX}" y1="${lineY}" x2="${buildingX + buildingWidth}" y2="${lineY}" stroke="black" stroke-width="3"/>`;
    }

    // Add roof based on roof style - EXACT CARBON COPY
    if (showRoof) {
      const roofY = buildingY - 60;
      
      if (roofStyle === 'pitched') {
        // Pitched roof (PA, PN)
        svgContent += `
          <polygon points="${buildingX},${buildingY} ${buildingX + buildingWidth/2},${roofY} ${buildingX + buildingWidth},${buildingY}"
                   fill="none" stroke="black" stroke-width="2"/>
          <line x1="${buildingX + buildingWidth/2}" y1="${roofY}" x2="${buildingX + buildingWidth/2}" y2="${buildingY}" 
                stroke="black" stroke-width="1"/>
        `;
      } else if (roofStyle === 'flat') {
        // Flat roof with parapet walls
        svgContent += `
          <rect x="${buildingX}" y="${buildingY - 15}" width="${buildingWidth}" height="15" 
                fill="#d3d3d3" stroke="black" stroke-width="2"/>
          <rect x="${buildingX - 5}" y="${buildingY - 25}" width="5" height="25" 
                fill="white" stroke="black" stroke-width="1"/>
          <rect x="${buildingX + buildingWidth}" y="${buildingY - 25}" width="5" height="25" 
                fill="white" stroke="black" stroke-width="1"/>
          <circle cx="${buildingX + buildingWidth * 0.25}" cy="${buildingY - 7}" r="3" fill="black"/>
          <circle cx="${buildingX + buildingWidth * 0.75}" cy="${buildingY - 7}" r="3" fill="black"/>
        `;
      } else if (roofStyle === 'thatch') {
        // Thatch roof (PT) - curved appearance
        svgContent += `
          <path d="M ${buildingX} ${buildingY} Q ${buildingX + buildingWidth/2} ${roofY - 10} ${buildingX + buildingWidth} ${buildingY}" 
                fill="none" stroke="black" stroke-width="2"/>
          <line x1="${buildingX + buildingWidth/2}" y1="${roofY}" x2="${buildingX + buildingWidth/2}" y2="${buildingY}" 
                stroke="black" stroke-width="1"/>
        `;
      } else if (roofStyle === 'sloping') {
        // Sloping roof (PS) - single slope
        svgContent += `
          <polygon points="${buildingX},${roofY} ${buildingX + buildingWidth},${buildingY} ${buildingX},${buildingY}"
                   fill="none" stroke="black" stroke-width="2"/>
        `;
      }
    }

    // EXACT CARBON COPY: Add placed elements with enhanced visuals and ID labels (from placedElements data)
    if (elevationData.placedElements && elevationData.placedElements.length > 0) {
      const currentElements = elevationData.placedElements.filter((element: any) => element.elevation === elevationType);
      
      currentElements.forEach((element: any, index: number) => {
        // Generate element ID
        const sameTypeElements = currentElements.filter((el: any) => el.type === element.type);
        const elementIndex = sameTypeElements.findIndex((el: any) => el.id === element.id) + 1;
        let elementId = '';
        
        switch (element.type) {
          case 'door':
            elementId = `D${elementIndex}`;
            // Enhanced door with panels and handle
            svgContent += `
              <g>
                <rect x="${element.x - 20}" y="${element.y - 40}" width="40" height="80" 
                      fill="#d97706" stroke="#92400e" stroke-width="2" rx="2"/>
                <!-- Door panels -->
                <rect x="${element.x - 16}" y="${element.y - 35}" width="32" height="30" 
                      fill="none" stroke="#92400e" stroke-width="1" rx="1"/>
                <rect x="${element.x - 16}" y="${element.y}" width="32" height="30" 
                      fill="none" stroke="#92400e" stroke-width="1" rx="1"/>
                <!-- Door handle -->
                <circle cx="${element.x + 12}" cy="${element.y - 5}" r="2" fill="#92400e"/>
                <!-- Door ID label -->
                <circle cx="${element.x - 15}" cy="${element.y - 30}" r="8" fill="#d97706" stroke="white" stroke-width="1"/>
                <text x="${element.x - 15}" y="${element.y - 26}" text-anchor="middle" font-size="10" fill="white" font-weight="bold">${elementId}</text>
              </g>
            `;
            break;
            
          case 'window':
            elementId = `W${elementIndex}`;
            // Use fixed dimensions like PropertyElevationDiagram (40x30)
            const windowWidth = 40;
            const windowHeight = 30;
            svgContent += `
              <g>
                <!-- Window main frame -->
                <rect x="${element.x - 20}" y="${element.y - 15}" width="${windowWidth}" height="${windowHeight}" 
                      fill="rgba(135, 206, 250, 0.8)" stroke="#1e40af" stroke-width="2" rx="2"/>
                <!-- Window cross pattern -->
                <line x1="${element.x}" y1="${element.y - 15}" x2="${element.x}" y2="${element.y + 15}" 
                      stroke="#1e40af" stroke-width="1"/>
                <line x1="${element.x - 20}" y1="${element.y}" x2="${element.x + 20}" y2="${element.y}" 
                      stroke="#1e40af" stroke-width="1"/>
                <!-- Window ID label -->
                <circle cx="${element.x - 15}" cy="${element.y - 10}" r="8" fill="#1e40af" stroke="white" stroke-width="1"/>
                <text x="${element.x - 15}" y="${element.y - 6}" text-anchor="middle" font-size="8" fill="white" font-weight="bold">${elementId}</text>
              </g>
            `;
            break;
            
          case 'airvent':
            elementId = `V${elementIndex}`;
            svgContent += `
              <g>
                <rect x="${element.x - 8}" y="${element.y - 4}" width="16" height="8" 
                      fill="#475569" stroke="#334155" stroke-width="1" rx="2"/>
                <!-- Vent slats -->
                <line x1="${element.x - 6}" y1="${element.y - 2}" x2="${element.x + 6}" y2="${element.y - 2}" stroke="#e2e8f0" stroke-width="1"/>
                <line x1="${element.x - 6}" y1="${element.y}" x2="${element.x + 6}" y2="${element.y}" stroke="#e2e8f0" stroke-width="1"/>
                <line x1="${element.x - 6}" y1="${element.y + 2}" x2="${element.x + 6}" y2="${element.y + 2}" stroke="#e2e8f0" stroke-width="1"/>
                <!-- Vent ID label -->
                <circle cx="${element.x + 6}" cy="${element.y - 6}" r="5" fill="#475569" stroke="white" stroke-width="1"/>
                <text x="${element.x + 6}" y="${element.y - 3}" text-anchor="middle" font-size="7" fill="white" font-weight="bold">${elementId}</text>
              </g>
            `;
            break;
        }
      });
    } else if (elevationData.elements) {
      // Fallback to elements data with default positioning
      // Add doors - Match PropertyElevationDiagram styling
      if (elevationData.elements.doors) {
        elevationData.elements.doors.forEach((door: any, index: number) => {
          const doorX = buildingX + 50 + (index * 60);
          const doorY = buildingY + (buildingHeight * storyHeight) - 80;
          svgContent += `
            <g>
              <rect x="${doorX - 15}" y="${doorY - 25}" width="30" height="50" 
                    fill="#d97706" stroke="#92400e" stroke-width="2" rx="2"/>
              <!-- Door panels -->
              <rect x="${doorX - 12}" y="${doorY - 22}" width="24" height="20" 
                    fill="none" stroke="#92400e" stroke-width="1" rx="1"/>
              <rect x="${doorX - 12}" y="${doorY - 2}" width="24" height="20" 
                    fill="none" stroke="#92400e" stroke-width="1" rx="1"/>
              <!-- Door handle -->
              <circle cx="${doorX + 8}" cy="${doorY}" r="2" fill="#92400e"/>
              <!-- Door ID label -->
              <circle cx="${doorX - 10}" cy="${doorY - 15}" r="8" fill="#d97706" stroke="white" stroke-width="1"/>
              <text x="${doorX - 10}" y="${doorY - 11}" text-anchor="middle" font-size="8" fill="white" font-weight="bold">D${index + 1}</text>
            </g>
          `;
        });
      }

      // Add windows - Match PropertyElevationDiagram exact styling
      if (elevationData.elements.windows) {
        elevationData.elements.windows.forEach((window: any, index: number) => {
          const windowX = buildingX + 150 + (index * 80);
          const windowY = buildingY + (buildingHeight * storyHeight) / 2;
          svgContent += `
            <g>
              <!-- Window main frame -->
              <rect x="${windowX - 20}" y="${windowY - 15}" width="40" height="30" 
                    fill="rgba(135, 206, 250, 0.8)" stroke="#1e40af" stroke-width="2" rx="2"/>
              <!-- Window cross pattern -->
              <line x1="${windowX}" y1="${windowY - 15}" x2="${windowX}" y2="${windowY + 15}" 
                    stroke="#1e40af" stroke-width="1"/>
              <line x1="${windowX - 20}" y1="${windowY}" x2="${windowX + 20}" y2="${windowY}" 
                    stroke="#1e40af" stroke-width="1"/>
              <!-- Window ID label -->
              <circle cx="${windowX - 15}" cy="${windowY - 10}" r="8" fill="#1e40af" stroke="white" stroke-width="1"/>
              <text x="${windowX - 15}" y="${windowY - 6}" text-anchor="middle" font-size="8" fill="white" font-weight="bold">W${index + 1}</text>
            </g>
          `;
        });
      }

      // Add air vents
      if (elevationData.elements.airVents) {
        elevationData.elements.airVents.forEach((vent: any, index: number) => {
          const ventX = buildingX + 30 + (index * 40);
          const ventY = buildingY + (buildingHeight * storyHeight) - 10;
          svgContent += `
            <rect x="${ventX - 6}" y="${ventY - 3}" width="12" height="6" 
                  fill="black" rx="1"/>
          `;
        });
      }
    }

    // No title on diagram - title is handled by the PDF section header

    svgContent += '</svg>';
    return svgContent;
  }

  /**
   * Generate simplified brown brickwork pattern SVG
   */
  private static generateCavityBrickworkSVGExact(x: number, y: number, width: number, height: number): string {
    const brickWidth = 30;
    const brickHeight = 12;
    const mortarWidth = 1;
    let pattern = '';
    
    // Simplified brown brickwork pattern across full width
    for (let row = 0; row < Math.floor(height / brickHeight); row++) {
      const offsetX = (row % 2) * (brickWidth / 2);
      const currentY = y + (row * brickHeight);
      
      for (let col = 0; col < Math.ceil((width + brickWidth) / brickWidth); col++) {
        const currentX = x + offsetX + (col * brickWidth);
        if (currentX < x + width && currentX + brickWidth > x) {
          const brickStartX = Math.max(currentX, x);
          const brickEndX = Math.min(currentX + brickWidth - mortarWidth, x + width);
          const brickActualWidth = brickEndX - brickStartX;
          
          if (brickActualWidth > 0) {
            pattern += `<rect x="${brickStartX}" y="${currentY}" width="${brickActualWidth}" height="${brickHeight - mortarWidth}" fill="white" stroke="#8B4513" stroke-width="0.5"/>`;
          }
        }
      }
    }
    
    return pattern;
  }

  /**
   * Generate wall insulation diagram based on insulation type
   */
  private static generateWallInsulationDiagram(insulationType: string): string {
    if (!insulationType) return '';

    const diagramTitle = this.getInsulationTypeTitle(insulationType);
    const svgContent = this.generateInsulationSVG(insulationType);

    return `
      <div class="wall-insulation-diagram">
        <div class="section-header insulation-header">
          <h3 class="section-title">${diagramTitle}</h3>
        </div>
        <div class="diagram-content">
          ${svgContent}
        </div>
      </div>
    `;
  }

  /**
   * Get insulation type title
   */
  private static getInsulationTypeTitle(insulationType: string): string {
    switch (insulationType) {
      case 'E': return 'External Wall Insulation (EWI)';
      case 'I': return 'Internal Wall Insulation (IWI)';
      case 'A': return 'As Built Construction';
      case 'U': return 'Unknown Insulation Type';
      default: return 'Wall Construction';
    }
  }

  /**
   * Generate SVG diagram for insulation type
   */
  private static generateInsulationSVG(insulationType: string): string {
    const baseWallWidth = 200;
    const wallHeight = 120;
    const svgWidth = 280;
    const svgHeight = 160;
    
    switch (insulationType) {
      case 'E': // External Wall Insulation
        return `
          <svg width="${svgWidth}" height="${svgHeight}" viewBox="0 0 ${svgWidth} ${svgHeight}">
            <!-- Outer insulation layer -->
            <rect x="40" y="20" width="30" height="${wallHeight}" fill="#FFE4B5" stroke="#D2691E" stroke-width="2"/>
            <!-- Main wall structure -->
            <rect x="70" y="20" width="${baseWallWidth}" height="${wallHeight}" fill="#F5F5DC" stroke="#8B4513" stroke-width="2"/>
            <!-- Internal finish -->
            <rect x="270" y="20" width="15" height="${wallHeight}" fill="#FFFFFF" stroke="#696969" stroke-width="1"/>
            
            <!-- Labels -->
            <text x="55" y="155" text-anchor="middle" font-size="10" fill="#333">Insulation</text>
            <text x="170" y="155" text-anchor="middle" font-size="10" fill="#333">Main Wall</text>
            <text x="277" y="155" text-anchor="middle" font-size="10" fill="#333">Internal</text>
            
            <!-- Dimension lines -->
            <line x1="40" y1="15" x2="285" y2="15" stroke="#666" stroke-width="1"/>
            <text x="162" y="12" text-anchor="middle" font-size="9" fill="#666">External Wall Insulation</text>
          </svg>
        `;
        
      case 'I': // Internal Wall Insulation
        return `
          <svg width="${svgWidth}" height="${svgHeight}" viewBox="0 0 ${svgWidth} ${svgHeight}">
            <!-- External finish -->
            <rect x="40" y="20" width="15" height="${wallHeight}" fill="#DCDCDC" stroke="#696969" stroke-width="1"/>
            <!-- Main wall structure -->
            <rect x="55" y="20" width="${baseWallWidth}" height="${wallHeight}" fill="#F5F5DC" stroke="#8B4513" stroke-width="2"/>
            <!-- Internal insulation layer -->
            <rect x="255" y="20" width="30" height="${wallHeight}" fill="#FFE4B5" stroke="#D2691E" stroke-width="2"/>
            
            <!-- Labels -->
            <text x="47" y="155" text-anchor="middle" font-size="10" fill="#333">External</text>
            <text x="155" y="155" text-anchor="middle" font-size="10" fill="#333">Main Wall</text>
            <text x="270" y="155" text-anchor="middle" font-size="10" fill="#333">Insulation</text>
            
            <!-- Dimension lines -->
            <line x1="40" y1="15" x2="285" y2="15" stroke="#666" stroke-width="1"/>
            <text x="162" y="12" text-anchor="middle" font-size="9" fill="#666">Internal Wall Insulation</text>
          </svg>
        `;
        
      case 'A': // As Built
        return `
          <svg width="${svgWidth}" height="${svgHeight}" viewBox="0 0 ${svgWidth} ${svgHeight}">
            <!-- External finish -->
            <rect x="40" y="20" width="15" height="${wallHeight}" fill="#DCDCDC" stroke="#696969" stroke-width="1"/>
            <!-- Main wall structure with cavity -->
            <rect x="55" y="20" width="90" height="${wallHeight}" fill="#F5F5DC" stroke="#8B4513" stroke-width="2"/>
            <rect x="145" y="20" width="20" height="${wallHeight}" fill="#F0F8FF" stroke="#4682B4" stroke-width="1" stroke-dasharray="3,3"/>
            <rect x="165" y="20" width="90" height="${wallHeight}" fill="#F5F5DC" stroke="#8B4513" stroke-width="2"/>
            <!-- Internal finish -->
            <rect x="255" y="20" width="15" height="${wallHeight}" fill="#FFFFFF" stroke="#696969" stroke-width="1"/>
            
            <!-- Labels -->
            <text x="47" y="155" text-anchor="middle" font-size="10" fill="#333">External</text>
            <text x="100" y="155" text-anchor="middle" font-size="10" fill="#333">Outer</text>
            <text x="155" y="155" text-anchor="middle" font-size="10" fill="#333">Cavity</text>
            <text x="210" y="155" text-anchor="middle" font-size="10" fill="#333">Inner</text>
            <text x="262" y="155" text-anchor="middle" font-size="10" fill="#333">Internal</text>
            
            <!-- Dimension lines -->
            <line x1="40" y1="15" x2="270" y2="15" stroke="#666" stroke-width="1"/>
            <text x="155" y="12" text-anchor="middle" font-size="9" fill="#666">As Built Construction</text>
          </svg>
        `;
        
      case 'U': // Unknown
      default:
        return `
          <svg width="${svgWidth}" height="${svgHeight}" viewBox="0 0 ${svgWidth} ${svgHeight}">
            <!-- Main wall structure (generic) -->
            <rect x="40" y="20" width="${baseWallWidth}" height="${wallHeight}" fill="#F5F5F5" stroke="#808080" stroke-width="2" stroke-dasharray="5,5"/>
            
            <!-- Question mark overlay -->
            <text x="140" y="90" text-anchor="middle" font-size="48" fill="#B0B0B0" font-weight="bold">?</text>
            
            <!-- Labels -->
            <text x="140" y="155" text-anchor="middle" font-size="10" fill="#333">Unknown Wall Construction</text>
            
            <!-- Dimension lines -->
            <line x1="40" y1="15" x2="240" y2="15" stroke="#666" stroke-width="1"/>
            <text x="140" y="12" text-anchor="middle" font-size="9" fill="#666">Construction Type Unknown</text>
          </svg>
        `;
    }
  }

  /**
   * Generate wall insulation global calculations
   */
  private static generateWallInsulationCalculations(globalCalcs: any): string {
    if (!globalCalcs || Object.keys(globalCalcs).length === 0) {
      return '';
    }

    const totalWallArea = globalCalcs.totalWallAreaWholeHouse ? `${globalCalcs.totalWallAreaWholeHouse.toFixed(2)} m²` : 'N/A';
    const totalTreatableArea = globalCalcs.totalTreatableArea ? `${globalCalcs.totalTreatableArea.toFixed(2)} m²` : 'N/A';
    const totalUntreatableArea = globalCalcs.totalUntreatableArea ? `${globalCalcs.totalUntreatableArea.toFixed(2)} m²` : 'N/A';
    const dwellingArea = globalCalcs.totalDwellingArea ? `${globalCalcs.totalDwellingArea.toFixed(2)} m²` : 'N/A';
    const poptPercentage = globalCalcs.poptPercentage ? `${globalCalcs.poptPercentage.toFixed(1)}%` : 'N/A';

    return `
      <div class="wall-insulation-calculations">
        <div class="section-header insulation-header">
          <h3 class="section-title">Global Calculations</h3>
        </div>
        <div class="calculations-content">
          <div class="calculations-grid">
            <div class="calc-item">
              <span class="calc-label">Total Wall Area</span>
              <span class="calc-value">${totalWallArea}</span>
            </div>
            <div class="calc-item">
              <span class="calc-label">Treatable Area</span>
              <span class="calc-value">${totalTreatableArea}</span>
            </div>
            <div class="calc-item">
              <span class="calc-label">Untreatable Area</span>
              <span class="calc-value">${totalUntreatableArea}</span>
            </div>
            <div class="calc-item">
              <span class="calc-label">Total Dwelling Area</span>
              <span class="calc-value">${dwellingArea}</span>
            </div>
            <div class="calc-item">
              <span class="calc-label">POPT Percentage</span>
              <span class="calc-value">${poptPercentage}</span>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Generate Evidence pages with categorical organization
   */
  private static generateEvidencePages(siteNotesData: any, bestAddress: string, startPageNumber: number, logoDataUrl: string, totalPages: number): string {
    if (!siteNotesData?.formData?.evidencePhotos || siteNotesData.formData.evidencePhotos.length === 0) {
      return '';
    }

    const categories = [
      { id: 'exterior', label: 'Exterior' },
      { id: 'wall-construction', label: 'Wall Construction Type' },
      { id: 'wall-insulation', label: 'Wall Insulation' },
      { id: 'floor', label: 'Floor' },
      { id: 'roof-interior', label: 'Roof Interior' },
      { id: 'heating-system', label: 'Heating System' },
      { id: 'heating-controls', label: 'Heating Controls' },
      { id: 'windows-doors', label: 'Windows & Doors' },
      { id: 'ventilation', label: 'Ventilation' },
      { id: 'lighting', label: 'Lighting' },
      { id: 'renewables', label: 'Renewables' },
      { id: 'additional', label: 'Additional Images' }
    ];

    let pagesHTML = '';
    let currentPageNumber = startPageNumber;

    categories.forEach(category => {
      const categoryPhotos = siteNotesData.formData.evidencePhotos.filter((p: any) => p.category === category.id);
      
      if (categoryPhotos.length === 0) return; // Skip empty categories

      // Split photos into pages of 4
      const photosPerPage = 4;
      const pageCount = Math.ceil(categoryPhotos.length / photosPerPage);

      for (let pageIndex = 0; pageIndex < pageCount; pageIndex++) {
        const pagePhotos = categoryPhotos.slice(pageIndex * photosPerPage, (pageIndex + 1) * photosPerPage);
        const isFirstPageOfCategory = pageIndex === 0;

        pagesHTML += `
          <div class="pdf-page">
            <div class="header">
              <div class="header-content">
                <div class="header-left">
                  <h1 class="property-address">${bestAddress}</h1>
                  <p class="section-title">Evidence - ${category.label}</p>
                  <span class="page-number">Page ${currentPageNumber} of ${totalPages}</span>
                </div>
                <div class="header-right">
                  ${logoDataUrl ? `<img src="${logoDataUrl}" alt="Company Logo" class="header-logo" />` : ''}
                </div>
              </div>
            </div>
            <div class="header-separator"></div>

            <div class="content-section">
              ${isFirstPageOfCategory ? `<h2 class="section-heading">${category.label}</h2>` : `<h2 class="section-heading">${category.label} (continued)</h2>`}
              
              <div class="evidence-grid">
                ${pagePhotos.map((photo: any, index: number) => `
                  <div class="evidence-item">
                    <div class="evidence-image-container">
                      <img src="${photo.dataUrl}" alt="${category.label} ${pageIndex * photosPerPage + index + 1}" class="evidence-image" />
                    </div>
                    ${photo.description ? `<p class="evidence-description">${photo.description}</p>` : ''}
                    <p class="evidence-meta">Captured: ${new Date(photo.timestamp).toLocaleString()}</p>
                  </div>
                `).join('')}
              </div>
            </div>
          </div>
          <div class="page-break"></div>
        `;
        currentPageNumber++;
      }
    });

    return pagesHTML;
  }

  /**
   * Generate Property & Heating Information section with compact modern UI
   */
  private static generatePropertyHeatingInformationSection(siteNotesData: any, data: any): string {
    if (!siteNotesData) {
      return '<div class="note-text">No property or heating data available.</div>';
    }

    const formData = siteNotesData.formData || {};
    
    // Generate Property Information section
    const propertyInfoHTML = this.generatePropertyInformationTable(formData, data.propertyAddress);
    
    // Generate Heating Systems section
    const heatingSystemsHTML = this.generateHeatingSystemsTable(siteNotesData);
    
    // Generate Windows, Doors & Utilities section
    const buildingElementsHTML = this.generateBuildingElementsTable(formData);

    return `
      <div class="property-heating-container">
        ${propertyInfoHTML}
        ${heatingSystemsHTML}
        ${buildingElementsHTML}
      </div>
    `;
  }

  /**
   * Generate Property Information table section
   */
  private static generatePropertyInformationTable(formData: any, propertyAddress: string): string {
    const formatWallThickness = (value: any): string => {
      if (!value) return 'Not Specified';
      return `${value}mm`;
    };

    const formatAgeBand = (ageBand: any): string => {
      if (!ageBand) return 'Not Specified';
      const ageBandMap: Record<string, string> = {
        'A': 'A - Before 1900',
        'B': 'B - 1900-1929',
        'C': 'C - 1930-1949',
        'D': 'D - 1950-1966',
        'E': 'E - 1967-1975',
        'F': 'F - 1976-1982',
        'G': 'G - 1983-1990',
        'H': 'H - 1991-1995',
        'I': 'I - 1996-2002',
        'J': 'J - 2003-2006',
        'K': 'K - 2007-2011',
        'L': 'L - 2012 onwards'
      };
      return ageBandMap[ageBand] || this.formatModernText(ageBand);
    };

    const formatRoofType = (roofType: any): string => {
      if (!roofType) return 'Not Specified';
      const roofTypeMap: Record<string, string> = {
        'pitched-tile': 'Pitched - Tile',
        'pitched-slate': 'Pitched - Slate',
        'pitched-thatch': 'Pitched - Thatch',
        'pitched-metal': 'Pitched - Metal',
        'flat-felt': 'Flat - Felt',
        'flat-asphalt': 'Flat - Asphalt',
        'flat-membrane': 'Flat - Membrane',
        'flat-green': 'Flat - Green Roof',
        'mansard': 'Mansard',
        'other': 'Other',
        'PA': 'Pitched - Tile', // RdSAP codes
        'PB': 'Pitched - Slate',
        'PC': 'Pitched - Thatch',
        'FA': 'Flat - Felt',
        'FB': 'Flat - Asphalt'
      };
      return roofTypeMap[roofType] || this.formatModernText(roofType);
    };

    const formatWallTypeWithThickness = (wallType: any, wallThickness: any): string => {
      const typeText = this.formatModernText(wallType);
      const thicknessText = formatWallThickness(wallThickness);
      if (typeText !== 'Not Specified' && thicknessText !== 'Not Specified') {
        return `${typeText} - ${thicknessText}`;
      } else if (typeText !== 'Not Specified') {
        return typeText;
      } else if (thicknessText !== 'Not Specified') {
        return thicknessText;
      }
      return 'Not Specified';
    };

    const propertyData = [
      ['Property Type', this.formatModernText(formData.propertyType)],
      ['Detachment Type', this.formatModernText(formData.detachment)],
      ['Age Band', formatAgeBand(formData.ageBand)],
      ['Build Date', this.formatModernText(formData.buildDate)],
      ['Wall Type', this.formatModernText(formData.wallType)],
      ['Wall Thickness', formatWallThickness(formData.wallThickness)],
      ['Wall Type & Thickness', formatWallTypeWithThickness(formData.wallType, formData.wallThickness)],
      ['Party Wall', this.formatModernText(formData.partyWall)],
      ['Roof Type', formatRoofType(formData.roofType || formData.roofTypeRdSAP)],
      ['Roof Insulation', this.formatModernText(formData.roofInsulation)],
      ['Total Habitable Rooms', this.formatModernText(formData.totalHabitableRooms)],
      ['Total Heated Habitable Rooms', this.formatModernText(formData.totalHeatedHabitableRooms)],
      ['Total Lights X', this.formatModernText(formData.totalLightsX)],
      ['Total Lights O', this.formatModernText(formData.totalLightsO)],
      ['Total LEL', this.formatModernText(formData.totalLEL)],
      ['Region', this.formatModernText(formData.region)],
      ['Assessment Purpose', this.formatModernText(formData.assessmentPurpose)],
      ['Transaction Type', this.formatModernText(formData.transactionType)],
      ['Inspection Date', this.formatModernText(formData.inspectionDate)],
      ['Process Date', this.formatModernText(formData.processDate)]
    ].filter(([_, value]) => value !== 'Not Specified');

    // Split into two columns for better layout
    const midPoint = Math.ceil(propertyData.length / 2);
    const leftColumn = propertyData.slice(0, midPoint);
    const rightColumn = propertyData.slice(midPoint);

    const generateColumnTable = (data: [string, string][], side: 'left' | 'right') => {
      const rows = data.map(([label, value]: [string, string]) => `
        <tr>
          <td class="prop-label">${label}</td>
          <td class="prop-value">${value}</td>
        </tr>
      `).join('');

      return `
        <div class="property-column ${side}">
          <table class="property-info-table">
            <tbody>
              ${rows}
            </tbody>
          </table>
        </div>
      `;
    };

    return `
      <div class="info-section property-section">
        <div class="section-header property-header">
          <h3 class="section-title">Property Information</h3>
        </div>
        <div class="property-content">
          <div class="property-columns">
            ${generateColumnTable(leftColumn as [string, string][], 'left')}
            ${generateColumnTable(rightColumn as [string, string][], 'right')}
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Generate Heating Systems table section
   */
  private static generateHeatingSystemsTable(siteNotesData: any): string {
    const systems = siteNotesData.heatingSystems || [];
    const formData = siteNotesData.formData || {};
    const selectedPCDBSystem = siteNotesData.selectedPCDBSystem;
    
    // Debug logging to understand what data is available
    console.log('🔥 HEATING SYSTEMS DEBUG:', {
      systemsLength: systems.length,
      hasFormDataHeating: !!formData.heatingType,
      hasSelectedPCDBSystem: !!selectedPCDBSystem,
      systemsData: systems.map((s: any) => ({
        id: s.id,
        brand: s.heatingBrand,
        hasPcdbSystem: !!s.pcdbSystem,
        pcdbBrand: s.pcdbSystem?.brand,
        pcdbModel: s.pcdbSystem?.model
      })),
      selectedPCDBData: selectedPCDBSystem ? {
        brand: selectedPCDBSystem.brand,
        model: selectedPCDBSystem.model,
        type: selectedPCDBSystem.type
      } : null
    });
    
    if (systems.length === 0 && !formData.heatingType && !selectedPCDBSystem) {
      return `
        <div class="info-section heating-section">
          <div class="section-header heating-header">
            <h3 class="section-title">Heating Systems</h3>
          </div>
          <div class="no-data">No heating system data available</div>
        </div>
      `;
    }

    // Handle legacy selectedPCDBSystem if no heating systems array but PCDB system exists
    if (systems.length === 0 && selectedPCDBSystem) {
      console.log('🔥 Using legacy selectedPCDBSystem:', selectedPCDBSystem);
      
      const pcdbSystem = selectedPCDBSystem;
      
      // Enhanced PCDB data extraction for legacy system
      const heatingSource = this.formatModernText(pcdbSystem.type);
      const systemType = this.formatModernText(pcdbSystem.installation_type);
      const brand = this.formatModernText(pcdbSystem.brand);
      
      // Model with qualifier if available
      let model = this.formatModernText(pcdbSystem.model);
      if (pcdbSystem.model_qualifier) {
        model += ` (${this.formatModernText(pcdbSystem.model_qualifier)})`;
      }
      
      // Serial number or PCDB reference
      const serialNumber = this.formatModernText(pcdbSystem.pcdb_reference);
      
      // Fuel type from PCDB
      const fuelType = this.formatModernText(pcdbSystem.fuel);
      
      // Enhanced data array with PCDB information
      const systemData = [
        ['Heating Source', heatingSource],
        ['System Type', systemType],
        ['Brand', brand],
        ['Model', model],
        ['PCDB Reference', serialNumber],
        ['Fuel Type', fuelType],
        ['Winter Efficiency', pcdbSystem.sap_winter_seasonal_efficiency ? `${pcdbSystem.sap_winter_seasonal_efficiency}%` : 'Not Specified'],
        ['Summer Efficiency', pcdbSystem.sap_summer_seasonal_efficiency ? `${pcdbSystem.sap_summer_seasonal_efficiency}%` : 'Not Specified']
      ];
      
      // Add PCDB specific fields if available
      if (pcdbSystem.output_power_kw) {
        systemData.push(['Output Power', `${pcdbSystem.output_power_kw} kW`]);
      }
      if (pcdbSystem.efficiency_category) {
        systemData.push(['Efficiency Category', this.formatModernText(pcdbSystem.efficiency_category)]);
      }
      if (typeof pcdbSystem.condensing === 'boolean') {
        systemData.push(['Condensing', pcdbSystem.condensing ? 'Yes' : 'No']);
      }
      if (pcdbSystem.flue_type) {
        systemData.push(['Flue Type', this.formatModernText(pcdbSystem.flue_type)]);
      }
      if (typeof pcdbSystem.fan_assisted === 'boolean') {
        systemData.push(['Fan Assisted', pcdbSystem.fan_assisted ? 'Yes' : 'No']);
      }
      
      const filteredData = systemData.filter(([_, value]) => value !== 'Not Specified' && value !== '0');
      
      return `
        <div class="info-section heating-section">
          <div class="section-header heating-header">
            <h3 class="section-title">Heating System (PCDB)</h3>
          </div>
          <div class="heating-content">
            <div class="heating-system-column">
              <h4 class="system-header">Primary System (PCDB Database)</h4>
              <table class="heating-system-table">
                <tbody>
                  ${(filteredData as [string, string][]).map((item: [string, string]) => `
                    <tr>
                      <td class="heating-label">${item[0]}</td>
                      <td class="heating-value">${item[1]}</td>
        </tr>
                  `).join('')}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      `;
    }

    // Handle multiple heating systems if available
    if (systems.length > 0) {
      const systemsData = systems.slice(0, 3).map((system: any, index: number) => {
        const pcdbSystem = system.pcdbSystem || {};
        
        // Enhanced PCDB data extraction
        const heatingSource = this.formatModernText(pcdbSystem.type || system.heatingType);
        const systemType = this.formatModernText(pcdbSystem.installation_type || system.heatingSystemType);
        const brand = this.formatModernText(pcdbSystem.brand || system.heatingBrand);
        
        // Model with qualifier if available
        let model = this.formatModernText(pcdbSystem.model || system.heatingModel);
        if (pcdbSystem.model_qualifier) {
          model += ` (${this.formatModernText(pcdbSystem.model_qualifier)})`;
        }
        
        // Serial number or PCDB reference
        const serialNumber = this.formatModernText(system.heatingSerialNumber || pcdbSystem.pcdb_reference);
        
        // Fuel type from PCDB if available
        const fuelType = this.formatModernText(pcdbSystem.fuel || system.heatingFuelType);
        
        // Location
        const sourceLocation = this.formatModernText(system.heatingSourceLocation);
        
        // Enhanced data array with PCDB information
        const systemData = [
          ['Heating Source', heatingSource],
          ['System Type', systemType],
          ['Brand', brand],
          ['Model', model],
          ['Serial Number', serialNumber],
          ['Fuel Type', fuelType],
          ['Source Location', sourceLocation],
          ['Total Radiators', this.formatModernText(system.totalRads || '0')],
          ['TRVs', this.formatModernText(system.trvs || '0')],
          ['Winter Efficiency', pcdbSystem.sap_winter_seasonal_efficiency ? `${pcdbSystem.sap_winter_seasonal_efficiency}%` : (system.winterEfficiency ? `${system.winterEfficiency}%` : 'Not Specified')],
          ['Summer Efficiency', pcdbSystem.sap_summer_seasonal_efficiency ? `${pcdbSystem.sap_summer_seasonal_efficiency}%` : (system.summerEfficiency ? `${system.summerEfficiency}%` : 'Not Specified')]
        ];
        
        // Add PCDB specific fields if available
        if (pcdbSystem.output_power_kw) {
          systemData.push(['Output Power', `${pcdbSystem.output_power_kw} kW`]);
        }
        if (pcdbSystem.efficiency_category) {
          systemData.push(['Efficiency Category', this.formatModernText(pcdbSystem.efficiency_category)]);
        }
        if (typeof pcdbSystem.condensing === 'boolean') {
          systemData.push(['Condensing', pcdbSystem.condensing ? 'Yes' : 'No']);
        }
        if (pcdbSystem.flue_type) {
          systemData.push(['Flue Type', this.formatModernText(pcdbSystem.flue_type)]);
        }
        if (typeof pcdbSystem.fan_assisted === 'boolean') {
          systemData.push(['Fan Assisted', pcdbSystem.fan_assisted ? 'Yes' : 'No']);
        }
        
        const systemHeader = `System ${index + 1}${system.isPrimary ? ' (Primary)' : ''}${pcdbSystem.brand ? ' (PCDB)' : ''}`;
        
        return {
          header: systemHeader,
          data: systemData.filter(([_, value]) => value !== 'Not Specified' && value !== '0')
        };
      });

      const systemColumns = systemsData.map((system: any) => `
        <div class="heating-system-column">
          <h4 class="system-header">${system.header}</h4>
          <table class="heating-system-table">
            <tbody>
              ${system.data.map(([label, value]: [string, string]) => `
                <tr>
                  <td class="heating-label">${label}</td>
                  <td class="heating-value">${value}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      `).join('');

      return `
        <div class="info-section heating-section">
          <div class="section-header heating-header">
            <h3 class="section-title">Heating Systems</h3>
          </div>
          <div class="heating-content">
            <div class="heating-systems-grid">
              ${systemColumns}
            </div>
          </div>
        </div>
      `;
    }

    // Handle legacy single heating system
    const legacyHeatingData = [
      ['Heating Type', this.formatModernText(formData.heatingType)],
      ['System Type', this.formatModernText(formData.heatingSystemType)],
      ['Fuel Type', this.formatModernText(formData.heatingFuelType)],
      ['Control', this.formatModernText(formData.heatingControl)],
      ['Winter Efficiency', formData.winterEfficiency ? `${formData.winterEfficiency}%` : 'Not Specified'],
      ['Summer Efficiency', formData.summerEfficiency ? `${formData.summerEfficiency}%` : 'Not Specified'],
      ['Flow Temperature', formData.flowTemperature ? `${formData.flowTemperature}°C` : 'Not Specified']
    ].filter(([_, value]) => value !== 'Not Specified');

    if (legacyHeatingData.length === 0) return '';

    return `
      <div class="info-section heating-section">
        <div class="section-header heating-header">
          <h3 class="section-title">Heating System</h3>
        </div>
        <div class="heating-content">
          <table class="heating-legacy-table">
            <tbody>
              ${(legacyHeatingData as [string, string][]).map((item: [string, string]) => `
                <tr>
                  <td class="heating-label">${item[0]}</td>
                  <td class="heating-value">${item[1]}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      </div>
    `;
  }

  /**
   * Generate Building Elements (Windows, Doors, Utilities) table section
   */
  private static generateBuildingElementsTable(formData: any): string {
    const windowsData = [
      ['Total Windows', this.formatModernText(formData.totalWindows)],
      ['Glazing Type', this.formatModernText(formData.glazingType)],
      ['Window Type', this.formatModernText(formData.windowType)],
      ['Frame Type', this.formatModernText(formData.windowFrameType)],
      ['Glazing Thickness', this.formatModernText(formData.glazingThickness)],
      ['Missing Trickle Vents', this.formatModernText(formData.windowsMissingTrickleVents || formData.missingTrickleVents)],
      ['Missing Trickle Vents Location', this.formatModernText(
        formData.missingTrickleVentsLocation || 
        formData.windowsMissingTrickleVentsLocation ||
        formData.trickleVentsLocation ||
        formData.windowsTrickleVentsLocation
      )]
    ].filter(([_, value]) => value !== 'Not Specified');

    const doorsData = [
      ['Total Doors', this.formatModernText(formData.totalDoors)],
      ['Doors Without Undercuts', this.formatModernText(formData.doorsWithoutUndercuts)],
      ['Doors Without Undercuts Location', this.formatModernText(formData.doorsWithoutUndercutsLocation || formData.doorsWithoutUndercutLocation)],
      ['Draught Lobby', this.formatModernText(formData.draughtLobby)]
    ].filter(([_, value]) => value !== 'Not Specified');

    const ventilationData = [
      ['MEVs Kitchen', this.formatModernText(formData.mevsKitchen)],
      ['MEVs Bathroom', this.formatModernText(formData.mevsBathroom)],
      ['MEVs En Suite', this.formatModernText(formData.mevsEnSuite)],
      ['MEVs WC', this.formatModernText(formData.mevsWC)],
      ['Open Chimneys', this.formatModernText(formData.openChimneys)],
      ['Blocked Chimneys', this.formatModernText(formData.blockedChimneys)],
      ['Extract Fans', this.formatModernText(formData.intermittentExtractFans)],
      ['Passive Vents', this.formatModernText(formData.passiveVents)]
    ].filter(([_, value]) => value !== 'Not Specified');

    const utilitiesData = [
      ['Gas Meter Location', this.formatModernText(formData.gmLocation)],
      ['Electric Meter Location', this.formatModernText(formData.emLocations)],
      ['Stop Tap Location', this.formatModernText(formData.stapLocation)]
    ].filter(([_, value]) => value !== 'Not Specified');

    const generateSubTable = (data: [string, string][], title: string, className: string) => {
      if (data.length === 0) return '';
      
      return `
        <div class="building-element-table ${className}">
          <h4 class="element-title">${title}</h4>
          <table class="element-table">
            <tbody>
              ${data.map(([label, value]: [string, string]) => `
                <tr>
                  <td class="element-label">${label}</td>
                  <td class="element-value">${value}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        </div>
      `;
    };

    const allTablesHTML = [
      generateSubTable(windowsData as [string, string][], 'Windows & Glazing', 'windows'),
      generateSubTable(doorsData as [string, string][], 'Doors', 'doors'),
      generateSubTable(ventilationData as [string, string][], 'Ventilation', 'ventilation'),
      generateSubTable(utilitiesData as [string, string][], 'Utilities', 'utilities')
    ].filter(table => table !== '').join('');

    if (!allTablesHTML) return '';

    return `
      <div class="info-section building-elements-section">
        <div class="section-header elements-header">
          <h3 class="section-title">Building Elements & Utilities</h3>
        </div>
        <div class="elements-content">
          <div class="elements-grid">
            ${allTablesHTML}
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Generate formula and working calculations for all floors and extensions
   */
  private static generateAllFloorCalculations(data: ExportData, availableFloors: number[]): string {
    const { siteNotesData } = data;
    
    if (!siteNotesData?.floors || siteNotesData.floors.length === 0) {
      return '<div class="note-text">No calculation data available.</div>';
    }

    // Process all floors in siteNotesData (including extensions)
    const calculationSections = siteNotesData.floors.map((floor: any, index: number) => {
      const floorNumber = floor.id;
      const floorArea = parseFloat(floor.floorArea) || 0;
      const hlp = parseFloat(floor.hlp) || 0;
      const pw = parseFloat(floor.pw) || 0;
      
      // Skip floors with no data
      if (floorArea === 0 && hlp === 0 && pw === 0) {
        return '';
      }

      const floorTitle = this.capitalizeFloorName(
        `${floor.floorLevel === 'ground' ? 'Ground Floor' : 
          floor.floorLevel === 'first' ? '1st Floor' :
          floor.floorLevel === 'second' ? '2nd Floor' :
          floor.floorLevel === 'third' ? '3rd Floor' :
          floor.floorLevel || 'Floor'} - ${floor.buildingType === 'main building' ? 'Main Building' : 
          floor.buildingType.charAt(0).toUpperCase() + floor.buildingType.slice(1)}`
      );

    let areaCalc = '';
    let hlpCalc = '';
    let pwCalc = '';

      // Generate area calculation
      areaCalc = this.generateAreaCalculation(floor, floorArea, siteNotesData);
      
      // Generate HLP calculation
      if (hlp > 0) {
        hlpCalc = this.generateHLPCalculation(floor, hlp);
      }
      
      // Generate PW calculation  
      if (pw > 0) {
        pwCalc = this.generatePWCalculation(floor, pw);
      }

      return `
        <div class="floor-calculation-section">
          <div class="floor-calc-header">
            <span class="floor-calc-title">${floorTitle}</span>
          </div>
          <div class="calculation-grid">
            ${areaCalc ? `
              <div class="calc-item">
                <div class="calc-label">Area:</div>
                <div class="calc-formula">${areaCalc}</div>
              </div>
            ` : ''}
          </div>
        </div>
      `;
    }).filter(Boolean);

    return calculationSections.join('');
  }

  /**
   * Generate area calculation formula for a floor
   */
  private static generateAreaCalculation(floor: any, floorArea: number, siteNotesData: any): string {
    const validRooms = floor.rooms?.filter((room: any) => {
        const hasValidName = room.name && room.name.trim() !== '';
        const length = parseFloat(room.length) || 0;
        const width = parseFloat(room.width) || 0;
      const area = parseFloat(room.area) || 0;
      
        const hasDimensions = length > 0 && width > 0;
      const hasAreaValue = area > 0;
      const hasAnyValidData = hasDimensions || hasAreaValue;
      
      return hasValidName && hasAnyValidData;
    }) || [];

    // Handle highlighter extensions (no walls but has area)
    if (validRooms.length === 0 && floorArea > 0) {
      if (floor.highlighterBreakdown?.dimensions) {
        const { width, height } = floor.highlighterBreakdown.dimensions;
        return `${width.toFixed(2)}m × ${height.toFixed(2)}m = ${floorArea.toFixed(2)}m²`;
        } else {
        const estimatedDimensions = this.estimateRectangleDimensions(floorArea);
        return `${estimatedDimensions.width.toFixed(2)}m × ${estimatedDimensions.height.toFixed(2)}m = ${floorArea.toFixed(2)}m²`;
        }
    }
    
    // Handle multiple rooms
    if (validRooms.length > 1) {
        const roomCalcs = validRooms.map((room: any) => {
          const length = parseFloat(room.length) || 0;
          const width = parseFloat(room.width) || 0;
        const area = parseFloat(room.area) || 0;
        
          if (length > 0 && width > 0) {
          const calculatedArea = length * width;
          return `${length.toFixed(2)}m×${width.toFixed(2)}m=${calculatedArea.toFixed(2)}m²`;
        } else if (area > 0) {
          return `${area.toFixed(2)}m²`;
        }
        return '0.00m²';
      });

      // Check for main building with extension deductions
      const isMainBuilding = floor.buildingType === 'main building';
        if (isMainBuilding && siteNotesData?.floors) {
          const extensionFloors = siteNotesData.floors.filter((f: any) =>
          f.floorLevel === floor.floorLevel && 
            f.buildingType !== 'main building' &&
            f.floorArea && f.floorArea.trim() !== ''
          );
        
        if (extensionFloors.length > 0) {
          let extensionDeductions = 0;
          const extensionDetails: string[] = [];

          extensionFloors.forEach((extFloor: any) => {
            const extArea = parseFloat(extFloor.floorArea) || 0;
            if (extArea > 0) {
              extensionDeductions += extArea;
              extensionDetails.push(`${extArea.toFixed(2)}m² (${extFloor.buildingType})`);
            }
          });
          
          if (extensionDeductions > 0) {
            const baseCalc = `${roomCalcs.join(' + ')}`;
            const deductionText = extensionDetails.length > 1 
              ? `${extensionDeductions.toFixed(2)}m² (extensions)`
              : extensionDetails[0];
            return `${baseCalc} - ${deductionText} = ${floorArea.toFixed(2)}m²`;
          }
        }
      }
      
      return `${roomCalcs.join(' + ')} = ${floorArea.toFixed(2)}m²`;
    }
    
    // Handle single room
    if (validRooms.length === 1) {
        const room = validRooms[0];
        const length = parseFloat(room.length) || 0;
        const width = parseFloat(room.width) || 0;
      const area = parseFloat(room.area) || 0;
      
      if (length > 0 && width > 0) {
        return `${length.toFixed(2)}m × ${width.toFixed(2)}m = ${floorArea.toFixed(2)}m²`;
      } else if (area > 0) {
        return `${area.toFixed(2)}m²`;
      }
    }
    
    return floorArea > 0 ? `${floorArea.toFixed(2)}m²` : '';
  }

  /**
   * Generate HLP calculation formula
   */
  private static generateHLPCalculation(floor: any, hlp: number): string {
    if (floor.highlighterBreakdown?.hlpSegments && floor.highlighterBreakdown.hlpSegments.length > 0) {
      const segments = floor.highlighterBreakdown.hlpSegments.map((s: number) => `${s.toFixed(2)}m`);
      return `${segments.join(' + ')} = ${hlp.toFixed(2)}m`;
    } else {
      const hlpSegments = this.estimateWallSegments(hlp, 'HLP');
      return `${hlpSegments.map(s => `${s}m`).join(' + ')} = ${hlp.toFixed(2)}m`;
    }
  }

  /**
   * Generate PW calculation formula
   */
  private static generatePWCalculation(floor: any, pw: number): string {
    if (floor.highlighterBreakdown?.pwSegments && floor.highlighterBreakdown.pwSegments.length > 0) {
      const segments = floor.highlighterBreakdown.pwSegments.map((s: number) => `${s.toFixed(2)}m`);
      return `${segments.join(' + ')} = ${pw.toFixed(2)}m`;
        } else {
      const pwSegments = this.estimateWallSegments(pw, 'PW');
      return `${pwSegments.map(s => `${s}m`).join(' + ')} = ${pw.toFixed(2)}m`;
    }
  }

  /**
   * Estimate rectangle dimensions from area (helper function from exportUtils.ts)
   */
  private static estimateRectangleDimensions(area: number): { width: number; height: number } {
    const ratio = 1.618; // Golden ratio for aesthetically pleasing rectangles
    const width = Math.sqrt(area * ratio);
    const height = area / width;
    return { width, height };
  }

  /**
   * Estimate wall segments for HLP/PW calculations (helper function from exportUtils.ts)
   */
  private static estimateWallSegments(totalLength: number, wallType: string): string[] {
    const segmentCount = Math.max(1, Math.min(4, Math.floor(totalLength / 3)));
    const baseLength = totalLength / segmentCount;
    const variation = baseLength * 0.2;
    
    const segments: string[] = [];
    let remaining = totalLength;
    
    for (let i = 0; i < segmentCount - 1; i++) {
      const segment = baseLength + (Math.random() - 0.5) * variation;
      segments.push(segment.toFixed(2));
      remaining -= segment;
    }
    
    segments.push(Math.max(0, remaining).toFixed(2));
    return segments;
  }

  /**
   * Generate calculations for all floors
   */
  private static getAllFloorsData(data: ExportData): any[] {
    const { siteNotesData } = data;
    
    if (!siteNotesData?.floors) {
      return [];
    }
    
    return siteNotesData.floors.map((floor: any) => ({
      floorData: floor,
      floorName: `${floor.floorLevel === 'ground' ? 'Ground Floor' : 
                   floor.floorLevel === 'first' ? '1st Floor' :
                   floor.floorLevel === 'second' ? '2nd Floor' :
                   floor.floorLevel === 'third' ? '3rd Floor' :
                   floor.floorLevel || 'Floor'} - ${floor.buildingType === 'main building' ? 'Main Building' : 
                   floor.buildingType ? floor.buildingType.charAt(0).toUpperCase() + floor.buildingType.slice(1) : 'Building'}`,
      area: parseFloat(floor.floorArea) || 0,
      height: floor.height || 'N/A',
      hlp: parseFloat(floor.hlp) || 0,
      pw: parseFloat(floor.pw) || 0
    }));
  }

  /**
   * Generate complete HTML document from export data
   */
  public static async generateHTMLFromData(data: ExportData, logoDataUrl?: string, headerColor?: string, companyName?: string, companyWebsite?: string, companyPhone?: string, floorPlanPageCount: number = 0): Promise<string> {
    console.log('🖼️ EXPORT DEBUG: PuppeteerPDFService.generateHTMLFromData called with:', {
      hasLogo: !!logoDataUrl,
      logoDataLength: logoDataUrl?.length || 0,
      headerColor: headerColor || 'white',
      companyName: companyName || 'default',
      floorPlanPageCount
    });
    const {
      rooms,
      roomCustomLabels,
      propertyAddress,
      currentFloor,
      hlpLength,
      pwLength,
      totalArea,
      siteNotesData
    } = data;

    // Helper function to get best address
    const getBestAddress = (propAddress: string, siteAddress?: any) => {
      if (siteAddress) {
        // Handle object-style address
        if (typeof siteAddress === 'object' && siteAddress.houseNumber) {
          return `${siteAddress.houseNumber} ${siteAddress.street}, ${siteAddress.town} ${siteAddress.postcode}`;
        }
        // Handle string-style address
        if (typeof siteAddress === 'string' && siteAddress.trim() !== '') {
          return siteAddress;
        }
      }
      return propAddress || 'Property Assessment Report';
    };

    const bestAddress = getBestAddress(propertyAddress, siteNotesData?.formData?.address);

    // Calculate total pages using running counter for accurate page numbering
    let currentPageNum = 1;
    
    // 1. Floor plan pages
    currentPageNum += floorPlanPageCount;
    const firstContentPage = currentPageNum;
    
    // 2. Property Overview & Calculations page
    const propertyOverviewPageNum = currentPageNum;
    currentPageNum++;
    
    // 3. Equipment Summary (Radiators & Windows) page  
    const equipmentSummaryPageNum = currentPageNum;
    currentPageNum++;
    
    // 4. Wall Insulation pages
    let wallInsulationPageNum = 0;
    if (siteNotesData?.formData?.wallInsulationType || 
        (siteNotesData?.formData?.wallInsulationElevations && siteNotesData.formData.wallInsulationElevations.length > 0)) {
      wallInsulationPageNum = currentPageNum;
      currentPageNum++;
    }
    
    // 5. Room in Roof page (conditional - always show if hasRoomInRoof is yes)
    let rirPageNum = 0;
    if (siteNotesData?.formData?.hasRoomInRoof === 'yes') {
      rirPageNum = currentPageNum;
      currentPageNum++;
    }
    
    // 6. Property & Heating Information page
    const heatingInfoPageNum = currentPageNum;
    currentPageNum++;
    
    // 7. Evidence pages
    const evidenceStartPage = currentPageNum;
    let evidencePageCount = 0;
    if (siteNotesData?.formData?.evidencePhotos && siteNotesData.formData.evidencePhotos.length > 0) {
      const categories = [
        'exterior', 'wall-construction', 'wall-insulation', 'floor', 'roof-interior',
        'heating-system', 'heating-controls', 'windows-doors', 'ventilation',
        'lighting', 'renewables', 'additional'
      ];
      
      categories.forEach(categoryId => {
        const categoryPhotos = siteNotesData.formData.evidencePhotos?.filter((p: any) => p.category === categoryId) || [];
        if (categoryPhotos.length > 0) {
          const pagesForCategory = Math.ceil(categoryPhotos.length / 4);
          evidencePageCount += pagesForCategory;
        }
      });
      currentPageNum += evidencePageCount;
    }
    
    const totalPages = currentPageNum - 1;
    console.log(`📊 Total pages: ${totalPages} (Floor plans: ${floorPlanPageCount}, Content: ${totalPages - floorPlanPageCount})`);
    console.log(`📊 PAGE NUMBERING DEBUG:`, {
      firstContentPage,
      wallInsulationPageNum,
      rirPageNum,
      heatingInfoPageNum,
      evidenceStartPage,
      evidencePageCount,
      totalPages
    });

    // Get all floors data (only floors with actual floor plan data, not extensions)
    const allFloorsData = this.getAllFloorsData(data);
    
    // Calculate totals across all floors with actual floor plan data (matching exportUtils.ts logic)
    const allFloorPlans = data.allFloorPlans || { [data.currentFloor]: data.currentWalls };
    const availableFloors = Object.keys(allFloorPlans).map(Number).sort((a, b) => a - b);
    
    let totalAreaAcrossAllFloors = 0;
    const allFloorData = data.allFloorData || {};
    
    // Sum area from floors with actual floor plan data only (not extensions)
    availableFloors.forEach(floorNumber => {
      const floorData = allFloorData[floorNumber];
      if (floorData?.floorMetrics?.totalArea) {
        totalAreaAcrossAllFloors += floorData.floorMetrics.totalArea;
      } else if (floorNumber === data.currentFloor) {
        // Use current floor data if no floor metrics available
        totalAreaAcrossAllFloors += data.totalArea || 0;
          } else {
        // Fallback to site notes data for floors without floor metrics
        const siteNotesFloor = siteNotesData?.floors?.find(f => f.id === floorNumber);
        if (siteNotesFloor?.floorArea) {
          totalAreaAcrossAllFloors += parseFloat(siteNotesFloor.floorArea) || 0;
        }
      }
    });

    // Add extensions area to total - but ONLY for floors that don't have floor plan data
    // Extensions that have floor plans are already included in the main calculation above
    if (siteNotesData?.floors) {
      siteNotesData.floors.forEach((floor: any) => {
        // Skip floors that are already counted in availableFloors (have actual floor plan data)
        if (availableFloors.includes(floor.id)) {
          return;
        }
        
        // ONLY add extension floors that are purely site notes entries (no floor plan)
        // These would be extensions added manually in site notes without drawing them
        if (floor.floorArea && floor.floorArea.trim() !== '' && floor.buildingType === 'extension') {
          const extensionArea = parseFloat(floor.floorArea) || 0;
          if (extensionArea > 0) {
            console.log(`📊 PDF Export: Adding manual extension area ${extensionArea}m² from floor ${floor.id} (${floor.buildingType})`);
            totalAreaAcrossAllFloors += extensionArea;
          }
        }
      });
    }
    


    // Compute room height for current floor (match exportUtils.ts behavior)
    let roomHeightInfo = 'N/A';
    if (siteNotesData && siteNotesData.floors) {
      const currentFloorData = siteNotesData.floors.find((f: any) => f.id === currentFloor);
      if (currentFloorData && currentFloorData.height && currentFloorData.height.trim() !== '') {
        const heightValue = currentFloorData.height.trim();
        const floorHeight = parseFloat(heightValue);
        if (!isNaN(floorHeight) && floorHeight > 0) {
          // If height already includes 'm', use as-is. Otherwise append 'm'
          if (heightValue.includes('m') || heightValue.includes('M')) {
            roomHeightInfo = heightValue;
          } else {
            roomHeightInfo = `${heightValue}m`;
          }
        }
      }
    }



    return `
      <div class="pdf-page">
        <div class="header">
          <div class="header-content">
            <div class="header-left">
              <h1 class="property-address">${bestAddress}</h1>
              <p class="section-title">Property Overview</p>
              <span class="page-number">Page ${firstContentPage} of ${totalPages}</span>
            </div>
            <div class="header-right">
              ${logoDataUrl ? `<img src="${logoDataUrl}" alt="Company Logo" class="header-logo" />` : ''}
            </div>
          </div>
        </div>

        <div class="header-separator"></div>

        <div class="content-section">
          <h2 class="section-heading">Property Overview & Floor Calculations</h2>
          
          <div style="margin-bottom: 16pt;">
            <h3 style="font-size: 11pt; font-weight: 600; color: #374151; margin-bottom: 8pt;">Property Overview</h3>
            <div class="overview-grid">
              <div class="overview-item">
                <span class="overview-label">Total Area:</span>
                  <span class="overview-value">${totalAreaAcrossAllFloors?.toFixed(2) || 'N/A'} m²</span>
              </div>
              <div class="overview-item">
                <span class="overview-label">Total Floors:</span>
                  <span class="overview-value">${availableFloors.length || 'N/A'}</span>
              </div>
            </div>
          </div>
        </div>

        <div class="content-section">
          <h2 class="section-heading">All Floors - Dimensions & Calculations</h2>
          
          ${allFloorsData.length > 0 ? `
            <div class="modern-table-container">
              <table class="modern-calc-table">
              <thead>
                <tr>
                    <th class="floor-column">Floor Level</th>
                    <th class="data-column">Area (m²)</th>
                    <th class="data-column">Height (m)</th>
                    <th class="data-column">HLP (m)</th>
                    <th class="data-column">PWL (m)</th>
                </tr>
              </thead>
              <tbody>
                  ${allFloorsData.map((floor, index) => `
                    <tr class="table-row ${index % 2 === 0 ? 'even-row' : 'odd-row'}">
                      <td class="floor-name"><span class="floor-badge">${this.capitalizeFloorName(floor.floorName)}</span></td>
                      <td class="metric-value">${floor.area > 0 ? floor.area.toFixed(2) : '—'}</td>
                      <td class="metric-value">${floor.height !== 'N/A' ? floor.height : '—'}</td>
                      <td class="metric-value">${floor.hlp > 0 ? floor.hlp.toFixed(2) : '—'}</td>
                      <td class="metric-value">${floor.pw > 0 ? floor.pw.toFixed(2) : '—'}</td>
                  </tr>
                `).join('')}
              </tbody>
            </table>
            </div>
          ` : `
            <div class="note-text">No floor data available.</div>
          `}
        </div>

        <div class="content-section">
          <h2 class="section-heading">Formula & Working - All Floors</h2>
          <div class="calculations-container">
            ${this.generateAllFloorCalculations(data, availableFloors)}
        </div>
        </div>

        <div class="page-break"></div>

        <div class="pdf-page">
          <div class="header">
            <div class="header-content">
              <div class="header-left">
                <h1 class="property-address">${bestAddress}</h1>
                <p class="section-title">Equipment Summary</p>
                <span class="page-number">Page ${equipmentSummaryPageNum} of ${totalPages}</span>
              </div>
              <div class="header-right">
                ${logoDataUrl ? `<img src="${logoDataUrl}" alt="Company Logo" class="header-logo" />` : ''}
              </div>
            </div>
          </div>

          <div class="header-separator"></div>

        <div class="content-section">
            <h2 class="section-heading">Radiators & Windows Summary</h2>
            ${this.generateRadiatorsWindowsTable(data, siteNotesData)}
          </div>
        </div>

        <div class="page-break"></div>

        ${siteNotesData?.formData?.wallInsulationType || 
          (siteNotesData?.formData?.wallInsulationElevations && siteNotesData.formData.wallInsulationElevations.length > 0) ? 
          this.generateWallInsulationPages(siteNotesData, bestAddress, wallInsulationPageNum, logoDataUrl, totalPages) : ''}

        ${siteNotesData?.formData?.hasRoomInRoof === 'yes' ? 
          await this.generateRoomInRoofPage(siteNotesData, bestAddress, rirPageNum, logoDataUrl, totalPages) + '<div class="page-break"></div>' : ''}

        <div class="pdf-page">
          <div class="header">
            <div class="header-content">
              <div class="header-left">
                <h1 class="property-address">${bestAddress}</h1>
                <p class="section-title">Property & Heating Information</p>
                <span class="page-number">Page ${heatingInfoPageNum} of ${totalPages}</span>
              </div>
              <div class="header-right">
                ${logoDataUrl ? `<img src="${logoDataUrl}" alt="Company Logo" class="header-logo" />` : ''}
              </div>
            </div>
          </div>

          <div class="header-separator"></div>

        <div class="content-section">
            <h2 class="section-heading">Property & Heating Information Property Assessment Report</h2>
            ${this.generatePropertyHeatingInformationSection(siteNotesData, data)}
          </div>
        </div>

        ${siteNotesData?.formData?.evidencePhotos && siteNotesData.formData.evidencePhotos.length > 0 ?
          this.generateEvidencePages(siteNotesData, bestAddress || '', evidenceStartPage, logoDataUrl || '', totalPages)
          : ''}

        <style>
          .pdf-page {
            width: 210mm;
            height: 297mm;
            margin: 0;
            padding: 5mm;
            font-family: 'Helvetica', 'Arial', sans-serif;
            font-size: 10pt;
            color: #1E293B;
            background: white;
            box-sizing: border-box;
          }

          .header {
            background: white;
            border: 0.5pt solid #E5E7EB;
            border-radius: 8pt;
            padding: 8pt;
            margin-bottom: 6pt;
            box-shadow: 0.3pt 0.3pt 0pt #E5E7EB;
          }

          .header-content {
            display: flex;
            justify-content: space-between;
            align-items: center;
          }

          .header-left {
            display: flex;
            flex-direction: column;
            gap: 4pt;
            flex: 1;
          }

          .header-right {
            display: flex;
            align-items: center;
            justify-content: flex-end;
            flex-shrink: 0;
            margin-left: 12pt;
          }

          .header-logo {
            max-height: 32pt;
            max-width: 80pt;
            object-fit: contain;
            opacity: 0.9;
          }

          .property-address {
            font-size: 11pt;
            font-weight: bold;
            color: #111827;
            margin: 0 0 2pt 0;
          }

          .section-title {
            font-size: 9pt;
            color: #374151;
            margin: 0;
          }
          
          /* White text for section titles in gradient headers */
          .property-header .section-title,
          .heating-header .section-title,
          .elements-header .section-title {
            color: white !important;
          }

          .page-number {
            font-size: 9pt;
            color: #6B7280;
            font-weight: 500;
            margin-top: 2pt;
          }

          .header-separator {
            height: 0.3pt;
            background: #E5E7EB;
            margin: 6pt 0;
          }

          .content-section {
            margin-bottom: 16pt;
          }

          .section-heading {
            font-size: 11pt;
            font-weight: bold;
            color: #1E293B;
            margin: 0 0 8pt 0;
            padding-bottom: 4pt;
            border-bottom: 1pt solid ${headerColor || '#F59E0B'};
          }

          .overview-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 6pt;
            margin-bottom: 12pt;
          }

          .overview-item {
            display: flex;
            justify-content: space-between;
            padding: 4pt 8pt;
            background: #F8FAFC;
            border-radius: 4pt;
            border: 0.5pt solid #E2E8F0;
          }

          .overview-label {
            font-weight: 500;
            color: #475569;
          }

          .overview-value {
            font-weight: bold;
            color: #1E293B;
          }

          .room-table {
            width: 100%;
            border-collapse: collapse;
            margin: 0;
          }

          .room-table th {
            background: ${headerColor || '#F59E0B'};
            color: white;
            font-size: 10pt;
            font-weight: bold;
            padding: 6pt 8pt;
            text-align: left;
            border: 0.5pt solid #D97706;
          }

          .room-table td {
            font-size: 9pt;
            padding: 6pt 8pt;
            border: 0.5pt solid #E2E8F0;
            color: #1E293B;
          }

          .room-table tbody tr:nth-child(even) {
            background: #F8FAFC;
          }

          /* Modern Calculations Table */
          .modern-table-container {
            background: #FFFFFF;
            border-radius: 12pt;
            box-shadow: 0 2pt 8pt rgba(0, 0, 0, 0.1);
            overflow: hidden;
            border: 1pt solid #E5E7EB;
          }

          .modern-calc-table {
            width: 100%;
            border-collapse: separate;
            border-spacing: 0;
            margin: 0;
            font-size: 12pt;
          }

          .modern-calc-table thead tr {
            background: ${headerColor || '#F59E0B'};
          }

          .modern-calc-table th.floor-column {
            color: white;
            font-size: 14pt;
            font-weight: bold;
            padding: 10pt 12pt;
            text-align: left;
            border: none;
            width: 40%;
            border-top-left-radius: 12pt;
          }

          .modern-calc-table th.data-column {
            color: white;
            font-size: 12pt;
            font-weight: bold;
            padding: 10pt 8pt;
            text-align: center;
            border: none;
            width: 15%;
          }

          .modern-calc-table th.data-column:last-child {
            border-top-right-radius: 12pt;
          }

          .modern-calc-table .table-row {
            transition: background-color 0.2s ease;
          }

          .modern-calc-table .even-row {
            background: #F8FAFC;
          }

          .modern-calc-table .odd-row {
            background: #FFFFFF;
          }

          .modern-calc-table .table-row:hover {
            background: #FEF3C7 !important;
          }

          .modern-calc-table .floor-name {
            padding: 8pt 12pt;
            border: none;
            border-bottom: 0.5pt solid #E5E7EB;
            vertical-align: middle;
          }

          .modern-calc-table .floor-badge {
            display: inline-block;
            color: #1E293B;
            padding: 3pt 8pt;
            border: 1pt solid #E5E7EB;
            border-radius: 6pt;
            font-weight: 600;
            font-size: 11pt;
            letter-spacing: 0.5pt;
          }

          .modern-calc-table .metric-value {
            padding: 8pt;
            text-align: center;
            font-weight: 500;
            color: #1E293B;
            border: none;
            border-bottom: 0.5pt solid #E5E7EB;
            vertical-align: middle;
            font-family: 'Courier New', monospace;
            font-size: 11pt;
          }

          .modern-calc-table tbody tr:last-child .floor-name,
          .modern-calc-table tbody tr:last-child .metric-value {
            border-bottom: none;
          }

          /* Clean Items Summary Table */
          .clean-table-container {
            margin: 16pt 0;
          }

          .items-summary-table {
            width: 100%;
            border-collapse: collapse;
            margin: 0;
            font-size: 8pt;
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
            border: 1pt solid #E5E7EB;
          }

          .items-summary-table thead tr {
            background: #F8FAFC;
            border-bottom: 2pt solid #D1D5DB;
          }

          .items-summary-table th {
            color: #374151;
            font-weight: 600;
            padding: 10pt 6pt;
            text-align: left;
            border-right: 1pt solid #E5E7EB;
            font-size: 7pt;
            text-transform: uppercase;
            letter-spacing: 0.5pt;
          }

          .items-summary-table th:last-child {
            border-right: none;
          }

          .items-summary-table .col-floor { width: 11%; }
          .items-summary-table .col-room { width: 8%; }
          .items-summary-table .col-radiator { width: 12%; }
          .items-summary-table .col-window { width: 12%; }
          .items-summary-table .col-glazing { width: 14%; }
          .items-summary-table .col-gap { width: 9%; }
          .items-summary-table .col-frame { width: 10%; }
          .items-summary-table .col-draught { width: 11%; }
          .items-summary-table .col-orient { width: 13%; }

          .items-summary-table tbody tr {
            border-bottom: 1pt solid #F3F4F6;
          }

          .items-summary-table tbody tr:nth-child(even) {
            background: #FAFBFC;
          }

          .items-summary-table tbody tr:nth-child(odd) {
            background: #FFFFFF;
          }

          .items-summary-table td {
            padding: 8pt 6pt;
            border-right: 1pt solid #F3F4F6;
            vertical-align: top;
            font-size: 7pt;
            line-height: 1.4;
          }

          .items-summary-table td:last-child {
            border-right: none;
          }

          .items-summary-table .floor-location {
            font-weight: 500;
            color: #4B5563;
          }

          .items-summary-table .room-name {
            font-weight: 600;
            color: #1F2937;
          }

          .items-summary-table .item-cell {
            color: #374151;
          }

          /* Clean styling for single items */
          .single-item {
            font-weight: 500;
            color: #1F2937;
            line-height: 1.3;
          }

          .item-type {
            font-size: 6pt;
            color: #6B7280;
            font-weight: normal;
            text-transform: uppercase;
            letter-spacing: 0.3pt;
          }

          /* Clean styling for multiple items */
          .multi-item-header {
            font-weight: 600;
            color: #1F2937;
            font-size: 6pt;
            text-transform: uppercase;
            letter-spacing: 0.5pt;
            padding-bottom: 3pt;
            margin-bottom: 3pt;
            border-bottom: 0.5pt solid #E5E7EB;
          }

          .item-row {
            display: flex;
            align-items: center;
            margin-bottom: 2pt;
            padding: 1pt 0;
          }

          .item-row:last-child {
            margin-bottom: 0;
          }

          .row-num {
            display: inline-block;
            width: 12pt;
            height: 12pt;
            background: #F3F4F6;
            color: #6B7280;
            font-size: 6pt;
            font-weight: 600;
            text-align: center;
            line-height: 12pt;
            border-radius: 2pt;
            margin-right: 4pt;
            flex-shrink: 0;
          }

          .item-spec {
            flex: 1;
            font-weight: 500;
            color: #1F2937;
            margin-right: 4pt;
          }

          .item-desc {
            font-size: 6pt;
            color: #6B7280;
            text-transform: uppercase;
            letter-spacing: 0.3pt;
          }

          .empty-cell {
            color: #9CA3AF;
            font-style: italic;
            text-align: center;
            display: block;
            padding: 4pt 0;
          }

          /* Footer styling */
          .items-summary-table tfoot .totals-row {
            background: #F9FAFB;
            border-top: 2pt solid #D1D5DB;
            font-weight: 600;
            color: #374151;
          }

          .items-summary-table tfoot td {
            padding: 8pt 6pt;
            font-size: 7pt;
          }

          /* Evidence Pages Styles */
          .evidence-grid {
            display: grid;
            grid-template-columns: repeat(2, 1fr);
            gap: 15pt;
            margin-top: 15pt;
          }

          .evidence-item {
            break-inside: avoid;
            page-break-inside: avoid;
          }

          /* 
           * ⚙️ EVIDENCE IMAGE HEIGHT ADJUSTMENT:
           * TO CHANGE IMAGE SIZE: Modify the 'height' value below (line 4167)
           * Current: 315pt (reduced by 10% from 350pt base)
           * Recommended range: 280pt - 400pt
           * Smaller = more images per page, Larger = better detail
           */
          .evidence-image-container {
            width: 100%;
            height: 315pt;
            border: 0.5pt solid #E5E7EB;
            border-radius: 8pt;
            overflow: hidden;
            background: #F9FAFB;
          }

          .evidence-image {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }

          .evidence-description {
            margin-top: 6pt;
            font-size: 8pt;
            color: #374151;
            line-height: 1.4;
          }

          .evidence-meta {
            margin-top: 4pt;
            font-size: 7pt;
            color: #9CA3AF;
          }

          /* Property & Heating Information Styles */
          .property-heating-container {
            margin: 0;
            padding: 0;
            font-size: 8pt;
          }

          .info-section {
            margin-bottom: 16pt;
            border: 1pt solid #E5E7EB;
            border-radius: 6pt;
            overflow: hidden;
            background: white;
          }

          .section-header {
            padding: 6pt 10pt;
            font-weight: bold;
            color: white;
            font-size: 9pt;
          }

          .property-header {
            background: linear-gradient(135deg, #1E40AF 0%, #1E3A8A 100%);
            color: white;
            padding: 8pt 12pt;
            border-radius: 6pt 6pt 0 0;
            text-align: center;
            font-weight: 600;
          }

          .heating-header {
            background: linear-gradient(135deg, #059669 0%, #047857 100%);
            color: white;
            padding: 8pt 12pt;
            border-radius: 6pt 6pt 0 0;
            text-align: center;
            font-weight: 600;
          }

          .elements-header {
            background: linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%);
            color: white;
            padding: 8pt 12pt;
            border-radius: 6pt 6pt 0 0;
            text-align: center;
            font-weight: 600;
          }

          .insulation-header {
            background: linear-gradient(135deg, #DC2626 0%, #B91C1C 100%);
            color: white;
            padding: 8pt 12pt;
            border-radius: 6pt 6pt 0 0;
            text-align: center;
            font-weight: 600;
          }

          .section-title {
            margin: 0;
            font-size: 9pt;
            font-weight: 600;
          }

          /* Property Information Styles */
          .property-content {
            padding: 8pt;
          }

          .property-columns {
            display: flex;
            gap: 10pt;
          }

          .property-column {
            flex: 1;
          }

          .property-info-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 7pt;
          }

          .property-info-table td {
            padding: 3pt 6pt;
            border-bottom: 0.5pt solid #F3F4F6;
            vertical-align: top;
          }

          .prop-label {
            font-weight: 600;
            color: #1F2937;
            background: #F1F5F9;
            width: 45%;
          }

          .prop-value {
            color: #0F172A;
            background: #FFFFFF;
            width: 55%;
          }

          /* Heating Systems Styles */
          .heating-content {
            padding: 8pt;
          }

          .heating-systems-grid {
            display: flex;
            gap: 8pt;
            flex-wrap: wrap;
          }

          .heating-system-column {
            flex: 1;
            min-width: 120pt;
            border: 1pt solid #F3F4F6;
            border-radius: 4pt;
            overflow: hidden;
          }

          .system-header {
            background: #E2E8F0;
            color: #1E293B;
            padding: 4pt 6pt;
            margin: 0;
            font-size: 8pt;
            font-weight: 600;
            text-align: center;
          }

          .heating-system-table, .heating-legacy-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 7pt;
          }

          .heating-system-table td, .heating-legacy-table td {
            padding: 3pt 6pt;
            border-bottom: 0.5pt solid #F3F4F6;
            vertical-align: top;
          }

          .heating-label {
            font-weight: 600;
            color: #1E293B;
            background: #F8FAFC;
            width: 45%;
          }

          .heating-value {
            color: #0F172A;
            background: #FFFFFF;
            width: 55%;
          }

          /* Building Elements Styles */
          .elements-content {
            padding: 8pt;
          }

          .elements-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 8pt;
          }

          .building-element-table {
            border: 1pt solid #F3F4F6;
            border-radius: 4pt;
            overflow: hidden;
          }

          .element-title {
            background: #E2E8F0;
            color: #1E293B;
            padding: 4pt 6pt;
            margin: 0;
            font-size: 8pt;
            font-weight: 600;
            border-bottom: 1pt solid #CBD5E1;
          }

          .element-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 7pt;
          }

          .element-table td {
            padding: 3pt 6pt;
            border-bottom: 0.5pt solid #F9FAFB;
            vertical-align: top;
          }

          .element-label {
            font-weight: 600;
            color: #1E293B;
            background: #F8FAFC;
            width: 50%;
          }

          .element-value {
            color: #0F172A;
            background: #FFFFFF;
            width: 50%;
          }

          .no-data {
            padding: 12pt;
            text-align: center;
            color: #6B7280;
            font-style: italic;
            font-size: 7pt;
          }

          /* Building Fabric Table */
          .building-fabric-container {
            margin: 8pt 0 16pt 0;
            display: flex;
            justify-content: center;
          }

          .building-fabric-table {
            border-collapse: collapse;
            font-size: 7pt;
            font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif;
            margin: 0 auto;
            border-radius: 6pt;
            overflow: hidden;
            box-shadow: 0 1pt 3pt rgba(0,0,0,0.1);
          }

          .building-fabric-table th {
            background: linear-gradient(135deg, #1E293B 0%, #334155 100%);
            color: white;
            font-weight: bold;
            padding: 6pt 12pt;
            text-align: center;
            border: none;
            font-size: 7pt;
            letter-spacing: 0.3pt;
          }

          .building-fabric-table td {
            background: #F8FAFC;
            color: #1E293B;
            padding: 6pt 12pt;
            text-align: center;
            border: none;
            font-weight: 500;
            font-size: 7pt;
          }

          .building-fabric-table th:first-child {
            border-top-left-radius: 6pt;
          }

          .building-fabric-table th:last-child {
            border-top-right-radius: 6pt;
          }

          .building-fabric-table tr:last-child td:first-child {
            border-bottom-left-radius: 6pt;
          }

          .building-fabric-table tr:last-child td:last-child {
            border-bottom-right-radius: 6pt;
          }

          /* Page break styling */
          .page-break {
            page-break-before: always;
            height: 0;
            margin: 0;
            padding: 0;
          }

          /* Ultra-Compact Wall Insulation Assessment Styles */
          .wall-insulation-ultra-compact {
            margin: 0;
            padding: 0;
            font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
            font-size: 6pt;
            line-height: 1.2;
          }

          .wall-assessment-compact {
            background: #FAFBFC;
            border: 1pt solid #E1E5E9;
            border-radius: 6pt;
            overflow: hidden;
          }

          .assessment-header {
            background: linear-gradient(90deg, #1E293B, #334155);
            color: white;
            padding: 6pt 10pt;
            margin: 0;
          }

          .assessment-header h3 {
            margin: 0;
            font-size: 8pt;
            font-weight: 600;
            letter-spacing: 0.2pt;
          }

          .assessment-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 8pt;
            padding: 8pt;
            background: white;
            border-bottom: 1pt solid #E1E5E9;
            margin-bottom: 5pt;
          }

          .property-summary,
          .global-calculations {
            background: #F8FAFC;
            border: 1pt solid #E1E5E9;
            border-radius: 6pt;
            padding: 8pt;
          }

          .property-summary h4,
          .global-calculations h4 {
            margin: 0 0 6pt 0;
            font-size: 8pt;
            font-weight: 600;
            color: #475569;
            text-transform: uppercase;
            letter-spacing: 0.3pt;
          }

          .data-grid {
            display: flex;
            flex-direction: column;
            gap: 3pt;
          }

          .data-row {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 4pt 6pt;
            background: white;
            border-radius: 3pt;
            border: 0.5pt solid #E1E5E9;
          }

          .data-row .label {
            font-size: 7pt;
            color: #64748B;
            font-weight: 500;
          }

          .data-row .value {
            font-size: 7pt;
            color: #1E293B;
            font-weight: 600;
          }

          .data-row .value.red {
            color: #DC2626;
            background: #FEF2F2;
            padding: 2pt 4pt;
            border-radius: 3pt;
            font-size: 7pt;
          }

          .data-row .value.green {
            color: #059669;
            background: #F0FDF4;
            padding: 2pt 4pt;
            border-radius: 3pt;
            font-size: 7pt;
          }

          .data-row .value.blue {
            color: #2563EB;
            background: #EFF6FF;
            padding: 2pt 4pt;
            border-radius: 3pt;
            font-size: 7pt;
          }

          .elevations-compact {
            background: white;
            padding: 8pt;
          }

          .elevation-compact-card {
            border: 1pt solid #E1E5E9;
            border-radius: 8pt;
            margin-bottom: 12pt;
            overflow: hidden;
            width: 100%;
            box-sizing: border-box;
            min-height: 180pt;
          }

          .elevation-compact-card:last-child {
            margin-bottom: 0;
          }

          .elevation-page-break {
            page-break-after: always;
            break-after: always;
            -webkit-break-after: always;
            height: 0;
            margin: 0;
            padding: 0;
            display: block;
            clear: both;
          }

          .elevation-header-compact {
            background: #F1F5F9;
            padding: 8pt 12pt;
            display: flex;
            justify-content: space-between;
            align-items: center;
            border-bottom: 1pt solid #E1E5E9;
          }

          .elevation-type {
            font-size: 10pt;
            font-weight: 600;
            color: #374151;
            text-transform: capitalize;
          }

          .elevation-metrics-inline {
            display: flex;
            align-items: center;
            gap: 8pt;
            flex-wrap: wrap;
          }

          .metric {
            font-size: 8pt;
            font-weight: 500;
            color: #64748B;
            padding: 3pt 6pt;
            background: white;
            border: 0.5pt solid #D1D5DB;
            border-radius: 4pt;
            white-space: nowrap;
          }

          .metric.treatable[data-type="green"] {
            color: #059669;
            border-color: #10B981;
          }

          .elements-inline {
            display: flex;
            gap: 4pt;
            flex-wrap: wrap;
          }

          .el {
            font-size: 7pt;
            font-weight: 600;
            padding: 3pt 5pt;
            border-radius: 4pt;
            text-align: center;
            min-width: 20pt;
          }

          .el.doors {
            background: #FED7AA;
            color: #9A3412;
          }

          .el.windows {
            background: #DBEAFE;
            color: #1E40AF;
          }

          .el.vents {
            background: #E2E8F0;
            color: #475569;
          }

          .elevation-content-compact {
            display: grid;
            grid-template-columns: 280pt 1fr;
            gap: 12pt;
            padding: 10pt;
            background: #FAFBFC;
            min-height: 160pt;
          }

          .diagram-compact {
            display: flex;
            justify-content: center;
            align-items: center;
            background: white;
            border: 1pt solid #E1E5E9;
            border-radius: 6pt;
            padding: 8pt;
            height: fit-content;
          }

          .diagram-compact svg {
            width: 250pt;  /* LARGER DIAGRAMS: Increased to 250pt for much better readability */
            height: 190pt; /* LARGER DIAGRAMS: Increased to 190pt for much better readability */
            background: white;
            border-radius: 6pt;
            display: block;
            box-shadow: 0 2pt 8pt rgba(0, 0, 0, 0.1);
          }

          .elements-data-compact {
            display: flex;
            flex-direction: column;
            justify-content: flex-start;
            min-width: 0;
          }

          .elements-compact-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 7pt;
            line-height: 1.3;
            border-radius: 6pt;
            overflow: hidden;
          }

          .elements-compact-table th {
            background: #F1F5F9;
            color: #374151;
            padding: 5pt;
            text-align: center;
            font-weight: 600;
            font-size: 7pt;
            border: 0.5pt solid #E1E5E9;
            white-space: nowrap;
          }

          .elements-compact-table td {
            padding: 4pt 5pt;
            text-align: center;
            font-size: 7pt;
            border: 0.5pt solid #E1E5E9;
            background: white;
            white-space: nowrap;
          }

          .elements-compact-table .id {
            font-weight: 600;
            background: #F8FAFC;
          }

          .door-row .id {
            color: #9A3412;
            background: #FED7AA;
          }

          .window-row .id {
            color: #1E40AF;
            background: #DBEAFE;
          }

          .vent-row .id {
            color: #475569;
            background: #E2E8F0;
          }

          .no-elements-compact {
            font-size: 8pt;
            color: #9CA3AF;
            text-align: center;
            font-style: italic;
            padding: 15pt;
            background: #F9FAFB;
            border-radius: 6pt;
            border: 1pt solid #E1E5E9;
          }

          .elevations-compact {
            background: white;
            padding: 10pt;
          }

          /* Remove old elevation details styles - replaced by compact layout */
          
          /* Legacy compatibility styles */
          .wall-insulation-container {
            margin: 0;
            padding: 0;
            font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
          }

          .wall-insulation-single-column {
            width: 100%;
          }

          /* Removed all old calculation and element detail styles - replaced by compact integrated layout */

          /* Insulation Overview Table - Make more compact */
          .insulation-overview-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 7pt;
          }

          .insulation-overview-table td {
            padding: 2pt 4pt;
            border-bottom: 1pt solid #E5E7EB;
            font-size: 6pt;
          }

          .overview-label {
            font-weight: 600;
            color: #475569;
            width: 50%;
          }

          .overview-value {
            color: #1E293B;
            font-weight: 500;
          }

          .overview-content {
            padding: 4pt;
          }

          /* Combined Insulation Table */
          .insulation-combined-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 7pt;
          }

          .insulation-combined-table td {
            padding: 3pt 6pt;
            border-bottom: 1pt solid #E5E7EB;
            font-size: 6pt;
            vertical-align: top;
          }

          .overview-row td {
            background: #F8FAFC;
            font-weight: 600;
          }

          .elevation-row td {
            background: white;
          }

          .elevation-row .overview-label {
            font-weight: 600;
            color: #6366F1;
          }

          .elevation-details-compact {
            display: flex;
            flex-direction: column;
            gap: 2pt;
          }

          .area-info {
            display: flex;
            gap: 8pt;
            font-size: 5pt;
          }

          .wall-area {
            color: #1E293B;
            font-weight: 500;
          }

          .treatable-area {
            color: #059669;
            font-weight: 500;
          }

          .elements-info {
            display: flex;
            gap: 3pt;
            flex-wrap: wrap;
          }

          .element-badge {
            font-size: 4pt;
            padding: 1pt 3pt;
            border-radius: 2pt;
            font-weight: 600;
            text-align: center;
          }

          .element-badge.doors {
            background: #FED7AA;
            color: #92400E;
          }

          .element-badge.windows {
            background: #DBEAFE;
            color: #1E40AF;
          }

          .element-badge.vents {
            background: #F1F5F9;
            color: #334155;
          }

          .no-elements {
            font-size: 5pt;
            color: #9CA3AF;
            font-style: italic;
            padding: 2pt 6pt;
            background: #F9FAFB;
            border-radius: 4pt;
            border: 1pt solid #E5E7EB;
          }

          .wall-insulation-single-column {
            width: 100%;
          }

          /* Enhanced Modern Insulation Table */
          .wall-insulation-overview-enhanced {
            margin: 12pt 0;
            border-radius: 12pt;
            overflow: hidden;
            box-shadow: 0 4pt 20pt rgba(0, 0, 0, 0.08), 0 1pt 3pt rgba(0, 0, 0, 0.1);
          }

          .modern-section-header {
            background: ${headerColor || '#F59E0B'};
            padding: 12pt 16pt;
            border-radius: 12pt 12pt 0 0;
            margin: 0;
            position: relative;
          }

          .modern-section-header::after {
            content: '';
            position: absolute;
            bottom: 0;
            left: 0;
            right: 0;
            height: 3pt;
            background: linear-gradient(90deg, #3B82F6, #6366F1, #8B5CF6);
          }

          .modern-section-title {
            color: white;
            font-size: 11pt;
            font-weight: 600;
            margin: 0;
            text-align: center;
            letter-spacing: 0.5pt;
          }

          .enhanced-content {
            background: white;
            border: 1pt solid #E2E8F0;
            border-radius: 0 0 12pt 12pt;
            overflow: hidden;
          }

          .modern-insulation-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 7pt;
            line-height: 1.5;
          }

          .table-header th {
            background: linear-gradient(135deg, #1E293B 0%, #334155 100%);
            color: white;
            padding: 8pt 12pt;
            font-size: 7pt;
            font-weight: 600;
            text-align: center;
            border: none;
            letter-spacing: 0.3pt;
          }

          .header-property {
            width: 50%;
          }

          .header-calculations {
            width: 50%;
          }

          .overview-main-row td {
            padding: 12pt;
            vertical-align: top;
            border-bottom: 1pt solid #E2E8F0;
          }

          .property-info {
            background: linear-gradient(135deg, #FAFBFF 0%, #F1F5F9 100%);
            border-right: 1pt solid #E2E8F0;
          }

          .calculations-info {
            background: linear-gradient(135deg, #F0F9FF 0%, #E0F2FE 100%);
          }

          .info-grid, .calc-grid {
            display: flex;
            flex-direction: column;
            gap: 6pt;
          }

          .info-item, .calc-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 6pt 8pt;
            background: white;
            border-radius: 6pt;
            border: 1pt solid #E2E8F0;
            box-shadow: 0 1pt 3pt rgba(0, 0, 0, 0.05);
          }

          .info-label, .calc-label {
            font-weight: 600;
            color: #475569;
            font-size: 6pt;
            letter-spacing: 0.1pt;
          }

          .info-value, .calc-value {
            font-weight: 500;
            font-size: 6pt;
            color: #1E293B;
          }

          .calc-value.red {
            color: #DC2626;
            font-weight: 700;
            background: linear-gradient(135deg, #FEF2F2 0%, #FEE2E2 100%);
            padding: 2pt 4pt;
            border-radius: 3pt;
          }

          .calc-value.green {
            color: #059669;
            font-weight: 700;
            background: linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 100%);
            padding: 2pt 4pt;
            border-radius: 3pt;
          }

          .calc-value.blue {
            color: #2563EB;
            font-weight: 700;
            background: linear-gradient(135deg, #EFF6FF 0%, #DBEAFE 100%);
            padding: 2pt 4pt;
            border-radius: 3pt;
          }

          .elevation-detail-row {
            border-bottom: 1pt solid #F1F5F9;
            transition: background-color 0.2s ease;
          }

          .elevation-detail-row:hover {
            background: #FAFBFF;
          }

          .elevation-label {
            background: linear-gradient(135deg, #EDE9FE 0%, #DDD6FE 100%);
            color: #6366F1;
            font-weight: 600;
            padding: 8pt 12pt;
            font-size: 7pt;
            width: 30%;
            border-right: 1pt solid #E2E8F0;
            letter-spacing: 0.2pt;
          }

          .elevation-data {
            padding: 8pt 12pt;
            background: white;
          }

          .elevation-metrics {
            display: flex;
            flex-direction: column;
            gap: 6pt;
          }

          .metric-group {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 4pt 8pt;
            background: linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%);
            border-radius: 4pt;
            border: 1pt solid #E2E8F0;
            box-shadow: 0 1pt 2pt rgba(0, 0, 0, 0.05);
          }

          .metric-label {
            font-size: 6pt;
            color: #64748B;
            font-weight: 500;
            letter-spacing: 0.1pt;
          }

          .metric-value {
            font-size: 6pt;
            color: #1E293B;
            font-weight: 600;
          }

          .metric-value.green {
            color: #059669;
            background: linear-gradient(135deg, #F0FDF4 0%, #DCFCE7 100%);
            padding: 1pt 3pt;
            border-radius: 3pt;
          }

          .elements-badges {
            display: flex;
            gap: 4pt;
            margin-top: 6pt;
            flex-wrap: wrap;
          }

          .badge {
            font-size: 5pt;
            padding: 2pt 6pt;
            border-radius: 4pt;
            font-weight: 600;
            text-align: center;
            border: 1pt solid transparent;
            letter-spacing: 0.1pt;
          }

          .badge.doors {
            background: linear-gradient(135deg, #FED7AA 0%, #FDBA74 100%);
            color: #9A3412;
            border-color: #F97316;
          }

          .badge.windows {
            background: linear-gradient(135deg, #DBEAFE 0%, #BFDBFE 100%);
            color: #1E40AF;
            border-color: #3B82F6;
          }

          .badge.vents {
            background: linear-gradient(135deg, #F1F5F9 0%, #E2E8F0 100%);
            color: #475569;
            border-color: #64748B;
          }

          .elevations-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 8pt;
          }

          .elevations-table th {
            background: linear-gradient(135deg, #F8FAFC 0%, #E2E8F0 100%);
            color: #475569;
            font-weight: 600;
            padding: 8pt 6pt;
            text-align: left;
            border-bottom: 2pt solid #CBD5E1;
            font-size: 7.5pt;
          }

          .elevations-table td {
            padding: 8pt 6pt;
            border-bottom: 1pt solid #F1F5F9;
            vertical-align: top;
          }

          .elevation-type {
            font-weight: 600;
            color: #6366F1;
            text-transform: capitalize;
          }

          .elevation-area,
          .elevation-treatable {
            color: #1E293B;
            font-weight: 500;
            text-align: right;
          }

          .elevation-elements {
            max-width: 120pt;
          }

          .elements-summary {
            display: flex;
            flex-wrap: wrap;
            gap: 4pt;
          }

          .element-count {
            display: inline-block;
            padding: 2pt 6pt;
            border-radius: 12pt;
            font-size: 7pt;
            font-weight: 500;
            white-space: nowrap;
          }

          .element-count.doors {
            background: #FEF3C7;
            color: #D97706;
          }

          .element-count.windows {
            background: #DBEAFE;
            color: #2563EB;
          }

          .element-count.vents {
            background: #D1FAE5;
            color: #059669;
          }

          .no-elements {
            color: #9CA3AF;
            font-style: italic;
            font-size: 7pt;
          }

          .calculations-grid {
            display: grid;
            grid-template-columns: 1fr 1fr;
            gap: 12pt;
          }

          .calc-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 8pt;
            background: #F8FAFC;
            border-radius: 6pt;
          }

          .calc-label {
            font-weight: 600;
            color: #475569;
            font-size: 8pt;
          }

          .calc-value {
            font-weight: 600;
            color: #1E293B;
            font-size: 8pt;
          }

          /* Legacy calc-table for other tables */
          .calc-table {
            width: 100%;
            border-collapse: collapse;
            margin: 0;
          }

          .calc-table th {
            background: ${headerColor || '#F59E0B'};
            color: white;
            font-size: 10pt;
            font-weight: bold;
            padding: 6pt 8pt;
            text-align: left;
            border: 0.5pt solid #D97706;
          }

          .calc-table td {
            font-size: 9pt;
            padding: 6pt 8pt;
            border: 0.5pt solid #E2E8F0;
            color: #1E293B;
            vertical-align: top;
          }

          .calc-formula {
            white-space: normal;
            word-break: break-word;
          }

          /* Calculations Container */
          .calculations-container {
            display: flex;
            flex-direction: column;
            gap: 8pt;
          }

          .floor-calculation-section {
            background: #F8FAFC;
            border: 1pt solid #E5E7EB;
            border-radius: 8pt;
            overflow: hidden;
            margin-bottom: 6pt;
          }

          .floor-calc-header {
            background: ${headerColor || '#F59E0B'};
            padding: 6pt 10pt;
            border-bottom: 1pt solid #E5E7EB;
          }

          .floor-calc-title {
            color: white;
            font-weight: bold;
            font-size: 9pt;
            letter-spacing: 0.3pt;
          }

          .calculation-grid {
            padding: 8pt 10pt;
            display: grid;
            grid-template-columns: 1fr;
            gap: 4pt;
          }

          .calc-item {
            display: flex;
            justify-content: space-between;
            align-items: center;
            padding: 3pt 0;
            border-bottom: 0.5pt solid #E5E7EB;
          }

          .calc-item:last-child {
            border-bottom: none;
          }

          .calc-label {
            font-weight: 600;
            color: #374151;
            font-size: 8pt;
            min-width: 40pt;
          }

          .calc-item .calc-formula {
            color: #1E293B;
            font-family: 'Courier New', monospace;
            font-size: 8pt;
            text-align: right;
            white-space: normal;
            word-break: break-word;
            flex: 1;
            margin-left: 8pt;
          }

          .note-text {
            font-size: 9pt;
            color: #4B5563;
          }

          /* Room in Roof Table Styles */
          .rir-diagram-section {
            margin: 12pt 0;
          }

          .rir-diagram-section h3 {
            font-size: 10pt;
            font-weight: 600;
            color: #1E293B;
            margin-bottom: 8pt;
          }

          .rir-table {
            width: 100%;
            border-collapse: collapse;
            font-size: 8pt;
            margin: 8pt 0;
          }

          .rir-table thead {
            background-color: #F1F5F9;
          }

          .rir-table th {
            padding: 6pt 8pt;
            text-align: left;
            font-weight: 600;
            color: #475569;
            border: 1pt solid #CBD5E1;
          }

          .rir-table tbody tr {
            border-bottom: 1pt solid #E2E8F0;
          }

          .rir-table tbody tr:nth-child(even) {
            background-color: #F8FAFC;
          }

          .rir-table tbody tr:hover {
            background-color: #F1F5F9;
          }

          .rir-table td {
            padding: 5pt 8pt;
            border: 1pt solid #E2E8F0;
            color: #1E293B;
          }

          .rir-table td:first-child {
            font-weight: 500;
          }

          @media print {
            .pdf-page {
              margin: 0;
              box-shadow: none;
              page-break-after: always;
            }
            .pdf-page:last-child {
              page-break-after: auto;
            }
          }
        </style>
      </div>
    `;
  }
}

export default PuppeteerPDFService;
